import { makeAutoObservable, ObservableMap, set, action, reaction, runInAction } from 'mobx';
import React from "react";
import { useContext } from "react";
import { ApolloClient } from "@apollo/client";
import { ClippingPolygonType, isDoorType, isSlidingDoorType, isDoubleDoorType, isWindowType, isVoidType, SymbolType, WallShapeType, WallType, otherModifiedPath, AlignmentLineType, JointPolygonData, OutlineSegment, OutlineSegmentCollection, FillPattern, LineType } from '../types/floorplannerTypes';
import type { FloorplannerStore } from "./floorplannerStore";
import type { EditorStoreType } from "./editorStore";
import * as THREE from "three";
import { composePaths } from "../components/FloorPlan/composeLines";
import { trimIntersectingLines, trimIntersectingShapes } from "../components/FloorPlan/trimIntersectingLines";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { invalidate } from '@react-three/fiber';
import { EditFloorplanDocument, EditFloorplanMutation } from '../gql';
import { dataUrlToFile } from '../utils/dataUrlToFile';
import { BoundingBox } from '../components/FloorPlan/SelectableSymbol';
// wallUpdateManager is imported lazily to avoid circular dependency
import { WallChangeDetector } from '../utils/wallChangeDetection';
import PerformanceMonitor from '../utils/PerformanceMonitor';
import { jointDetector, JointInfo } from '../utils/JointDetector';
import { jointCoordinator } from '../utils/JointCoordinator';
import { wallGeometryCache, WallGeometryCache } from '../utils/WallGeometryCache';
import { multiJointRenderer } from '../utils/MultiJointRenderer';
import { populateDefaultCorners, updatePathPoint } from '../utils/wallCornerHelpers';
import { ensureWallHasVersion, wallNeedsGeometryRebuild } from '../utils/wallVersioning';
import { createJointPolygon } from '../utils/jointPolygonGenerator';
import { createOutlineSegments } from '../utils/outlineSegmentation';
import { disposeAllPatternTextures } from '../utils/patternTextures';
import { disposeAllPatternFillMaterials } from '../components/FloorPlan/PatternFillMaterial';
import { getSymbolInsertGeometry, getSymbolRenderTotalWidth } from '../utils/symbolAttachmentGeometry';
import { composeExportWithTitleBlock, ExportAnnotationOptions } from '../utils/exportTitleBlock';
import { getSafeExportRenderSize } from '../utils/exportRenderSizing';
import { cropExportPreviewImage, ExportPreviewFraming } from '../utils/exportPreviewCrop';
import { isFloorplannerDebugLoggingEnabled } from '../utils/debugLogging';

let boundFloorplannerStore: FloorplannerStore | null = null;
export const bindFloorplannerStore = (store: FloorplannerStore) => {
  boundFloorplannerStore = store;
};
export const getFloorplannerStore = (): FloorplannerStore => {
  if (!boundFloorplannerStore) {
    throw new Error("renderStore: floorplannerStore not bound");
  }
  return boundFloorplannerStore;
};

// Lazy accessor to avoid circular dependency with editorStore
let cachedEditorStore: EditorStoreType | null = null;
let editorStoreLookupWarned = false;

/**
 * Returns the globally cached editorStore instance.
 */
export const getEditorStore = (): EditorStoreType | null => {
  if (!cachedEditorStore) {
    const globalEditorStore = (globalThis as typeof globalThis & { __boniEditorStore?: EditorStoreType }).__boniEditorStore;
    if (globalEditorStore) {
      cachedEditorStore = globalEditorStore;
    } else if (!editorStoreLookupWarned && isFloorplannerDebugLoggingEnabled()) {
      console.warn("editorStore is not yet available when requested from renderStore");
      editorStoreLookupWarned = true;
    }
  }
  return cachedEditorStore;
};

// Module-level Set for tracking pending outline generations
// This is intentionally outside the class to avoid MobX tracking entirely
// which prevents React "setState during render" warnings
const pendingOutlineGenerations = new Set<string>();

export type TargetFormatType = {
  format: string;
  orientation: 'portrait' | 'landscape';
  width: number;
  height: number;
  shiftX?: number;
  shiftY?: number;
  zoom?: number;
}

/**
 * Options that control trimming behavior for wall shapes.
 */
type TrimShapesOptions = {
  /**
   * Forces trimming even if the wall is in progressive mode. This should be
   * used sparingly when an immediate geometry rebuild is required (for
   * example, after detaching walls where cached fill geometry must be reset).
   */
  force?: boolean;
};

type AttachmentDragPreview = {
  wallId: string | null;
  position: THREE.Vector2;
  rotation: number;
  positionFromStart?: number;
};

/**
 * UI / control objects that must not be cloned for off-screen export renders.
 * TransformControls.clone() constructs without a camera; updateMatrixWorld then crashes.
 * Selection outlines use stencil meshes that fill solid blue in the off-screen renderer.
 */
export function shouldSkipObjectForSceneClone(object: THREE.Object3D): boolean {
  const typed = object as THREE.Object3D & {
    isTransformControls?: boolean;
    isTransformControlsGizmo?: boolean;
    isTransformControlsPlane?: boolean;
  };
  return Boolean(
    object.userData?.excludeFromSceneClone ||
      typed.isTransformControls ||
      typed.isTransformControlsGizmo ||
      typed.isTransformControlsPlane ||
      object.type === "TransformControls" ||
      object.type === "TransformControlsGizmo" ||
      object.type === "TransformControlsPlane",
  );
}

class RenderStore {
  client: ApolloClient<any> | undefined;
  scene: THREE.Scene | undefined;
  cameraZ: number = 20;
  wallClippingsMap = new Map<string, ClippingPolygonType[]>();
  innerWallShapesMap = new Map<string, WallShapeType[]>();
  outline1WallShapesMap = new Map<string, WallShapeType[]>();
  outline2WallShapesMap = new Map<string, WallShapeType[]>();
  endCapsWallShapesMap = new Map<string, WallShapeType[]>();
  alignmentLinesMap = new ObservableMap<string, AlignmentLineType>();
  alignmentLinesApproxMap = new ObservableMap<string, AlignmentLineType>();
  labelBackgroundColor: THREE.Color = new THREE.Color(0xffffff);
  imageData: string | undefined;
  rendering = false;
  isDragging = false;
  isDirty = false;
  blockDirty = false;
  progressiveModeEnabled = false;
  isReconstructingShapes = false;
  safeToRedraw = true;
  deferredUpdateDepth: number = 0;
  deferredDirtyWalls: Set<string> = new Set();
  allowVisualUpdatesForWalls: Set<string> = new Set();
  deferredSnappingResults: Map<string, any> = new Map();
  private jointProcessingDepth = 0;
  private multiJointRenderingDepth = 0;
  private deferredInvalidationTimeout: number | null = null;
  private pendingInvalidations: Set<string> = new Set();

  // Error tracking and performance monitoring properties
  geometryErrors = new Map<string, { count: number, lastError: string, timestamp: number }>();
  performanceAlerts = new Set<string>();
  operationMetrics = { trimOperations: 0, shapeCompositions: 0, errors: 0, totalTime: 0 };
  errorRecoveryAttempts = new Map<string, number>();
  wallsNeedingFallback?: Set<string>;

  jointsMap = new Map<string, JointInfo>();
  jointPolygonsMap = new ObservableMap<string, JointPolygonData>();
  outlineSegmentsMap = new ObservableMap<string, OutlineSegmentCollection>();
  private suppressedAttachmentOutlineGaps = new Map<string, Set<string>>();
  attachmentDragPreviews = new ObservableMap<string, AttachmentDragPreview>();

  // Cache performance metrics
  jointPolygonCacheHits = 0;
  jointPolygonCacheMisses = 0;
  lastBoundingBoxStats?: {
    meshCount: number;
    validBoxCount: number;
    skippedCount: number;
    size: { x: number; y: number; z: number };
    isValidSize: boolean;
    isEmpty: boolean;
    timestamp: number;
  };

  // Debug logging flag (can be toggled independently of editorStore to avoid cross-store imports)
  debugLogging = false;

  /** Latest captureSnapshot call wins; older async completions must not overwrite imageData. */
  private captureSnapshotSeq = 0;
  private captureSnapshotActive = 0;

  // Group translate mode state - for optimized group drag without MobX updates during drag
  isGroupTranslating = false;
  groupTranslateOffset: { x: number; y: number } = { x: 0, y: 0 };
  translatingObjectIds: Set<string> = new Set();
  translatingWallIds: Set<string> = new Set();

  // Group rotate mode state - for optimized group rotation without MobX updates during drag
  isGroupRotating = false;
  groupRotationAngle = 0;
  groupRotationCenter: { x: number; y: number } = { x: 0, y: 0 };
  rotatingObjectIds: Set<string> = new Set();
  rotatingWallIds: Set<string> = new Set();

  constructor() {
    makeAutoObservable(this);
    // Note: wallUpdateManager will be initialized lazily to avoid circular dependency
  }

  dispose() {
    // Dispose pattern textures and materials
    disposeAllPatternTextures();
    disposeAllPatternFillMaterials();
  }

  initialize(client: ApolloClient<any>) {
    this.client = client;
  }

  getClippingPolygon = (symbol: SymbolType, wall: WallType): ClippingPolygonType => {
    const objectWidth = isDoorType(symbol)
      ? symbol.doorWidth + ((symbol.doorFrameWidth || getFloorplannerStore().doorFrameWidth)) * 2
      : isSlidingDoorType(symbol)
        ? symbol.doorWidth * 2 + ((symbol.doorFrameWidth || getFloorplannerStore().doorFrameWidth)) * 2
      : isDoubleDoorType(symbol)
        ? (symbol.doubleDoorWidth + (symbol.doorFrameWidth || getFloorplannerStore().doorFrameWidth)) * 2
        : isWindowType(symbol)
          ? symbol.windowLength
          : isVoidType(symbol)
            ? symbol.voidLength
            : 0.8;

    // Ensure minimum valid dimensions to prevent triangulation errors
    const minDimension = 0.001; // 1mm in world units

    // For arc walls use the straight-insert chord direction/midpoint.
    // For straight walls use the traditional chord-based approach.
    let objectDirection: THREE.Vector2;
    let symbolPositionOnWall: THREE.Vector2;

    // Find the attachment positionFromStart for this symbol on this wall
    const attachment = wall.symbolAttachments?.find(a => a.symbolId === symbol.id);
    const posFromStart = attachment?.positionFromStart ?? (() => {
      // Fallback: project symbol position onto chord
      const wallDir = wall.end.clone().sub(wall.start).normalize();
      return symbol.position.clone().sub(wall.start).dot(wallDir);
    })();
    const insertGeom = getSymbolInsertGeometry(wall, symbol, posFromStart, getFloorplannerStore().doorFrameWidth);
    const renderObjectWidth = Math.max(
      getSymbolRenderTotalWidth(wall, symbol, posFromStart, getFloorplannerStore().doorFrameWidth),
      minDimension,
    );
    const objectLength = renderObjectWidth / 2;
    objectDirection = insertGeom.tangent.clone();
    symbolPositionOnWall = insertGeom.midpoint.clone();

    // For clipping curved walls effectively, the clipping polygon depth must cover the wall thickness 
    // and any potential bulge of the curve away from the chord.
    const wallThickness = wall.wallWidth || getFloorplannerStore().wallWidth;
    // We use a generously large depth to ensure it fully intersects the curved wall body
    const clippingDepth = Math.max(objectWidth, wallThickness * 4);
    
    // The midpoint from getSymbolInsertGeometry is exactly the center of the cutout.
    // No need to add any manual half-door offsets.
    const objectPositionOffset = symbolPositionOnWall.clone();
    const objectLengthVector = objectDirection.clone().multiplyScalar(objectLength);
    const objectWidthVector = objectDirection.clone().rotateAround(new THREE.Vector2(0, 0), Math.PI / 2).multiplyScalar(clippingDepth / 2);
    
    const topLeft = objectPositionOffset.clone().add(objectLengthVector).add(objectWidthVector);
    const topRight = objectPositionOffset.clone().add(objectLengthVector).sub(objectWidthVector);
    const bottomRight = objectPositionOffset.clone().sub(objectLengthVector).sub(objectWidthVector);
    const bottomLeft = objectPositionOffset.clone().sub(objectLengthVector).add(objectWidthVector);

    // CCW Winding order is crucial for polygon-clipping algorithms
    const polygon = [
      new THREE.Vector2(topLeft.x, topLeft.y),
      new THREE.Vector2(bottomLeft.x, bottomLeft.y),
      new THREE.Vector2(bottomRight.x, bottomRight.y),
      new THREE.Vector2(topRight.x, topRight.y),
    ];

    // Validate polygon to prevent triangulation errors
    const bounds = new THREE.Box2().setFromPoints(polygon);
    if (bounds.min.distanceTo(bounds.max) < minDimension) {
      console.warn('Clipping polygon has invalid dimensions, using fallback for symbol:', symbol.id);
      // Create a small square fallback polygon
      const fallbackSize = minDimension;
      return {
        belongsTo: symbol.id,
        polygon: [
          new THREE.Vector2(symbol.position.x - fallbackSize / 2, symbol.position.y - fallbackSize / 2),
          new THREE.Vector2(symbol.position.x + fallbackSize / 2, symbol.position.y - fallbackSize / 2),
          new THREE.Vector2(symbol.position.x + fallbackSize / 2, symbol.position.y + fallbackSize / 2),
          new THREE.Vector2(symbol.position.x - fallbackSize / 2, symbol.position.y + fallbackSize / 2),
        ],
        objectType: isVoidType(symbol) ? "void" : "symbol",
      };
    }

    const clippingPolygon: ClippingPolygonType = {
      belongsTo: symbol.id,
      polygon,
      objectType: isVoidType(symbol) ? "void" : "symbol",
    };
    return clippingPolygon;
  };

  addWallClipping = (wallId: string, clipping: ClippingPolygonType) => {
    // Validate clipping polygon has non-zero area to prevent rendering errors
    // Uses the same threshold as fillMeshWalls.ts (0.0001)
    if (clipping.polygon.length >= 3) {
      // Calculate area using shoelace formula
      let area = 0;
      for (let i = 0; i < clipping.polygon.length; i++) {
        const j = (i + 1) % clipping.polygon.length;
        area += clipping.polygon[i].x * clipping.polygon[j].y;
        area -= clipping.polygon[j].x * clipping.polygon[i].y;
      }
      area = Math.abs(area) / 2;

      if (area <= 0.0001) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[renderStore] Skipping zero-area clipping polygon for wall ${wallId} from ${clipping.belongsTo} ` +
            `(objectType: ${clipping.objectType}, area: ${area.toFixed(6)})`
          );
        }
        return false;
      }
    } else {
      // Polygon has fewer than 3 vertices
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `[renderStore] Skipping invalid clipping polygon for wall ${wallId} from ${clipping.belongsTo} ` +
          `(objectType: ${clipping.objectType}, vertices: ${clipping.polygon.length})`
        );
      }
      return false;
    }

    const wallClippings = this.wallClippingsMap.get(wallId) || [];
    const index = wallClippings.findIndex(c => c.belongsTo === clipping.belongsTo);
    const isNew = index === -1;
    if (index > -1) {
      wallClippings[index] = clipping;
    } else {
      wallClippings.push(clipping);
    }
    this.wallClippingsMap.set(wallId, wallClippings);
    if (this.debugLogging) {
      console.log(`    addWallClipping:`, {
        receivingWall: wallId,
        belongsTo: clipping.belongsTo,
        isNew,
        totalClippings: wallClippings.length,
      });
    }
    return true;
  }

  removeWallClipping = (wallId: string, belongsTo: string) => {
    const clippings = this.wallClippingsMap.get(wallId) || [];
    const index = clippings.findIndex(c => c.belongsTo === belongsTo);
    if (index > -1) {
      clippings.splice(index, 1);
    }
    this.wallClippingsMap.set(wallId, clippings);
  }

  suppressAttachmentOutlineGap = (wallId: string, symbolId: string) => {
    const suppressed = this.suppressedAttachmentOutlineGaps.get(wallId) ?? new Set<string>();
    suppressed.add(symbolId);
    this.suppressedAttachmentOutlineGaps.set(wallId, suppressed);
  }

  restoreAttachmentOutlineGap = (wallId: string, symbolId: string) => {
    const suppressed = this.suppressedAttachmentOutlineGaps.get(wallId);
    if (!suppressed) {
      return;
    }
    suppressed.delete(symbolId);
    if (suppressed.size === 0) {
      this.suppressedAttachmentOutlineGaps.delete(wallId);
    }
  }

  private getWallForOutlineGeneration = (wall: WallType): WallType => {
    const suppressed = this.suppressedAttachmentOutlineGaps.get(wall.id);
    if (!suppressed?.size || !wall.symbolAttachments?.length) {
      return wall;
    }

    return {
      ...wall,
      symbolAttachments: wall.symbolAttachments.filter((attachment) => !suppressed.has(attachment.symbolId)),
    };
  }

  setAttachmentDragPreview = (symbolId: string, preview: AttachmentDragPreview) => {
    this.attachmentDragPreviews.set(symbolId, {
      wallId: preview.wallId,
      position: preview.position.clone(),
      rotation: preview.rotation,
      positionFromStart: preview.positionFromStart,
    });
  }

  getAttachmentDragPreview = (symbolId: string): AttachmentDragPreview | undefined => {
    return this.attachmentDragPreviews.get(symbolId);
  }

  clearAttachmentDragPreview = (symbolId: string) => {
    this.attachmentDragPreviews.delete(symbolId);
  }

  clearWallClippings = (wallId: string) => {
    const allClippings = this.wallClippingsMap.get(wallId);
    if (allClippings) {
      allClippings.forEach((clipping) => {
        if (clipping.objectType === "wall") {
          this.removeWallClipping(wallId, clipping.belongsTo);
        }
      });
    }
  }

  clearSymbolClippings = (wallId: string) => {
    // Clean out all previous symbol clippings
    const allClippings = this.wallClippingsMap.get(wallId);
    if (allClippings) {
      allClippings.forEach((clipping) => {
        if (clipping.objectType === "symbol" || clipping.objectType === "door" || clipping.objectType === "slidingDoor" || clipping.objectType === "doubleDoor" || clipping.objectType === "window" || clipping.objectType === "void") {
          renderStore.removeWallClipping(wallId, clipping.belongsTo);
        }
      });
    }
  }

  clearClippings = (wallId?: string) => {
    if (wallId) {
      this.wallClippingsMap.delete(wallId);
      this.suppressedAttachmentOutlineGaps.delete(wallId);
    } else {
      this.wallClippingsMap.clear();
      this.suppressedAttachmentOutlineGaps.clear();
      this.attachmentDragPreviews.clear();
    }
  }

  /**
   * Returns all walls connected to the provided wall via either legacy center connections
   * or edge-based connections. Duplicates are removed to avoid redundant downstream work.
   */
  private getConnectedWallsList = (wall: WallType): WallType[] => {
    const connectedIds = new Set<string>();

    wall.centerConnections?.forEach(connection => {
      if (connection?.id && connection.id !== wall.id) {
        connectedIds.add(connection.id);
      }
    });

    wall.edgeConnections?.forEach(edgeConnection => {
      if (edgeConnection?.targetWallId && edgeConnection.targetWallId !== wall.id) {
        connectedIds.add(edgeConnection.targetWallId);
      }
    });

    const connectedWalls: WallType[] = [];
    connectedIds.forEach(id => {
      const connectedWall = getFloorplannerStore().wallsMap.get(id);
      if (connectedWall) {
        connectedWalls.push(connectedWall);
      }
    });

    return connectedWalls;
  }

  getConnectedShapes = (wallId: string, scene?: THREE.Scene, geometryCache?: WallGeometryCache): WallShapeType[] => {
    const wall = getFloorplannerStore().wallsMap.get(wallId);
    if (!wall) return [];
    const connectedShapes = [] as WallShapeType[];

    // During progressive mode, only return directly connected walls to prevent cascading updates
    // This limits the scope of connected shapes during drag operations for better performance
    const connectedWalls = this.getConnectedWallsList(wall);

    // Check if any connected wall is in progressive mode to limit scope
    const isAnyConnectedInProgressiveMode = connectedWalls.some(w =>
      this.isAnyWallInProgressiveMode() && this.getWallUpdateManager()?.isWallInProgressiveMode(w.id)
    );

    if (isAnyConnectedInProgressiveMode) {
      // During progressive mode, only include directly connected walls
      // Avoid deep nested shape calculations to prevent cascading drag updates
      connectedWalls.forEach(w => {
        if (!this.getWallUpdateManager()?.isWallInProgressiveMode(w.id)) {
          // Query cache first if provided, otherwise fall back to renderStore
          let innerShapes: WallShapeType[] | undefined;

          if (geometryCache) {
            // Get from cache - this returns the most current geometry during joint processing
            // The cache will fall back internally if geometry is not available
            innerShapes = geometryCache.getInnerShapes(w.id);
          } else {
            // Fall back to renderStore maps (backward compatibility)
            innerShapes = this.innerWallShapesMap.get(w.id);
            if (!innerShapes) {
              this.composeWallShape(w.id, scene);
              innerShapes = this.innerWallShapesMap.get(w.id);
            }
          }

          if (innerShapes) {
            connectedShapes.push(...innerShapes);
          }
        }
      });
    } else {
      // Normal mode: include all connected shapes
      connectedWalls.forEach(w => {
        // CRITICAL FIX: Check if connected wall participates in multi-joints
        // If it does, ensure its joints are processed BEFORE we use its geometry
        // This prevents render-order dependencies where clicking different walls
        // produces different geometry due to stale cached shapes
        const connectedWallJoints = this.getJointsForWall(w.id);
        const hasMultiJoints = connectedWallJoints.some(joint =>
          this.shouldUseCoordinatedProcessing(joint) && jointCoordinator.isJointDirty(joint.id)
        );

        if (hasMultiJoints && !geometryCache) {
          // Connected wall has dirty multi-joints that need processing
          // Process them now to ensure we get correct geometry
          for (const joint of connectedWallJoints) {
            if (this.shouldUseCoordinatedProcessing(joint) && jointCoordinator.isJointDirty(joint.id)) {
              this.processJoint(joint, scene);
            }
          }
        }

        // Query cache first if provided, otherwise fall back to renderStore
        let innerShapes: WallShapeType[] | undefined;

        if (geometryCache) {
          // Get from cache - this returns the most current geometry during joint processing
          // The cache will fall back internally if geometry is not available
          innerShapes = geometryCache.getInnerShapes(w.id);
        } else {
          // Fall back to renderStore maps (backward compatibility)
          innerShapes = this.innerWallShapesMap.get(w.id);
          if (!innerShapes) {
            this.composeWallShape(w.id, scene);
            innerShapes = this.innerWallShapesMap.get(w.id);
          }
        }

        if (innerShapes) {
          connectedShapes.push(...innerShapes);
        }
      });
    }

    return connectedShapes;
  }

  composeWallShape = (wallId: string, scene?: THREE.Scene, suppressFlags?: boolean): WallShapeType[] => {
    const startTime = performance.now();
    const operationId = `composeWallShape_${wallId}_${Date.now()}`;
    let compositionErrors = 0;

    // Only set flags if suppressFlags is false (default behavior)
    if (!suppressFlags) {
      // Set reconstruction flag to prevent intermediate renders
      this.setIsReconstructingShapes(true);
      // Set unsafe to redraw during reconstruction
      this.setSafeToRedraw(false);
    }

    try {
      // Track operation start
      this.operationMetrics.shapeCompositions++;

      // Validate wall exists
      const wall = getFloorplannerStore().wallsMap.get(wallId);
      if (!wall) {
        const errorMessage = `Wall not found for composition: ${wallId}`;
        console.error('renderStore: ' + errorMessage);
        this.handleGeometryError('composition', wallId, new Error(errorMessage));
        return [];
      }

      // Ensure wall has a geometry version for cache validation
      ensureWallHasVersion(wall);

      // CRITICAL: Check if we can use cached geometry based on version numbers
      // This prevents unnecessary rebuilds and ensures cache consistency
      const cached = wallGeometryCache.getWallGeometry(wallId);
      if (cached && !suppressFlags) {
        // Check if cached geometry is still valid
        if (wallGeometryCache.isCachedGeometryValid(wallId, wall, getFloorplannerStore())) {
          // Cache hit - use cached geometry
          if (this.debugLogging) {
            // Track cache hit (for wall geometry, not joint polygons)
            // Joint polygon cache hits are tracked separately in generateJointPolygons
          }

          // Use cached geometry - it's still valid
          this.innerWallShapesMap.set(wallId, cached.inner);
          this.outline1WallShapesMap.set(wallId, cached.outline1);
          this.outline2WallShapesMap.set(wallId, cached.outline2);
          this.endCapsWallShapesMap.set(wallId, cached.endcaps);

          // Process joints if needed (they might still be dirty even with valid geometry)
          // Skip during drag to prevent expensive cascading operations
          if (!suppressFlags && !this.isDragging) {
            this.handlePostCompositionJointProcessing(wallId, scene);
          }

          // ALWAYS generate joint polygons and outline segments
          // These are fast and needed for proper rendering
          if (!suppressFlags) {
            // First detect joints for this wall so generateJointPolygons has data
            this.detectAndStoreJointsForWall(wallId);
            this.generateJointPolygons(wallId);
            this.generateOutlineSegments(wallId);
          }

          return cached.inner;
        }
      }

      // Track memory usage before composition
      const memoryBefore = (performance as any).memory ? (performance as any).memory.usedJSHeapSize / (1024 * 1024) : 0;

      try {
        const innerShapes: WallShapeType[] = [];
        const outerShapes1: WallShapeType[] = [];
        const outerShapes2: WallShapeType[] = [];
        const endCaps: WallShapeType[] = [];

        const compositionStart = performance.now();

        composePaths(wallId, innerShapes, outerShapes1, outerShapes2, endCaps);

        const compositionTime = performance.now() - compositionStart;
        PerformanceMonitor.trackDragEvent(wallId, 'compose_paths', compositionTime);

        if (compositionTime > 20) {
          this.addPerformanceAlert(`Slow composition for wall ${wallId}: ${compositionTime.toFixed(2)}ms`);
        }

        // Validate composed shapes
        if (!innerShapes || innerShapes.length === 0) {
          this.handleGeometryError('composition', wallId, new Error('No inner shapes generated'));
          compositionErrors++;
        }

        this.innerWallShapesMap.set(wallId, innerShapes);
        this.outline1WallShapesMap.set(wallId, outerShapes1);
        this.outline2WallShapesMap.set(wallId, outerShapes2);
        this.endCapsWallShapesMap.set(wallId, endCaps);

        const totalTime = performance.now() - startTime;
        this.operationMetrics.totalTime += totalTime;

        // Track memory usage after composition
        const memoryAfter = (performance as any).memory ? (performance as any).memory.usedJSHeapSize / (1024 * 1024) : 0;
        const memoryIncrease = memoryAfter - memoryBefore;

        // Performance monitoring and alerts
        PerformanceMonitor.trackDragEvent(wallId, 'composeWallShape_complete', totalTime);

        if (totalTime > 10) {
          // slow composeWallShape operation
        }

        if (memoryIncrease > 20) { // More than 20MB increase (adjusted for Three.js operations)
          // high memory usage during shape composition
        }

        // Log successful composition
        if (totalTime > 5 || compositionErrors > 0) {
          // composeWallShape summary
        }

        if (!suppressFlags) {
          // Check if wall is in progressive mode (being constructed/dragged)
          // Skip expensive joint processing during progressive mode to prevent
          // cascading updates to other walls - full geometry is rebuilt on drag end
          // Also check isDragging as a fallback - the watchdog timer may have cleared
          // progressive mode even though drag is still active (e.g., after OS notification
          // interrupts and the 2-second watchdog fires, but user resumes dragging)
          const isInProgressiveMode = this.getWallUpdateManager()?.isWallInProgressiveMode(wallId) || this.isDragging;

          if (!isInProgressiveMode) {
            // Full processing for normal walls
            // This handles complex joint coordination and cascading updates
            this.handlePostCompositionJointProcessing(wallId, scene);
          }

          // ALWAYS detect joints for this wall - needed for joint polygon generation
          // Joint detection is fast and doesn't cascade to other walls
          // Without this, generateJointPolygons would find no joints during progressive mode
          this.detectAndStoreJointsForWall(wallId);

          // ALWAYS generate joint polygons for proper corner fills
          // This is fast and only updates the polygons for this wall's joints
          // Without this, corners appear as voids during drag/construction
          this.generateJointPolygons(wallId);

          // ALWAYS generate outline segments for the wall itself - needed for rendering
          // This is fast and doesn't cascade to other walls
          this.generateOutlineSegments(wallId);

          const processedInnerShapes = this.innerWallShapesMap.get(wallId);
          if (processedInnerShapes && processedInnerShapes.length > 0) {
            return processedInnerShapes;
          }
        }

        return innerShapes;
      } catch (compositionError) {
        const err = compositionError instanceof Error ? compositionError : new Error(String(compositionError));
        const errorContext = {
          wallId,
          operationId,
          error: err.message,
          duration: performance.now() - startTime
        };

        console.error('renderStore: Error in composePaths operation', errorContext);
        this.handleGeometryError('composition', wallId, err);
        PerformanceMonitor.trackError(wallId, 'composeWallShape', 'composition_error');
        compositionErrors++;

        // Attempt error recovery
        const recoverySuccess = this.attemptErrorRecovery(wallId, 'composition');
        if (recoverySuccess) {
          // Error recovery successful for composeWallShape
          // Return empty shapes to prevent further errors
          return [];
        } else {
          // Re-throw if recovery failed
          throw err;
        }
      }
    } catch (overallError) {
      const err = overallError instanceof Error ? overallError : new Error(String(overallError));
      const errorContext = {
        wallId,
        operationId,
        error: err.message,
        duration: performance.now() - startTime,
        compositionErrors
      };

      console.error('renderStore: Critical error in composeWallShape', errorContext);
      this.handleGeometryError('composition', wallId, err);
      PerformanceMonitor.trackError(wallId, 'composeWallShape', 'critical_error');
      this.operationMetrics.errors++;

      // Attempt graceful degradation
      try {
        // Attempting graceful degradation for composeWallShape

        // Return cached shapes if available
        const cachedShapes = this.innerWallShapesMap.get(wallId);
        if (cachedShapes && cachedShapes.length > 0) {
          // Using cached shapes for graceful degradation
          return cachedShapes;
        }

        // Return empty shapes as last resort
        return [];
      } catch (degradationError) {
        const degErr = degradationError instanceof Error ? degradationError : new Error(String(degradationError));
        console.error('renderStore: Graceful degradation failed for composeWallShape', {
          wallId,
          operationId,
          error: degErr.message
        });
        PerformanceMonitor.trackError(wallId, 'composeWallShape', 'degradation_failed');

        // Return empty shapes to prevent cascading errors
        return [];
      }
    } finally {
      // Only clear flags if suppressFlags is false and respect deferredUpdateDepth
      if (!suppressFlags) {
        // Always clear reconstruction flag and set safe to redraw, even if error occurred
        this.setIsReconstructingShapes(false);
        if (this.deferredUpdateDepth === 0) {
          this.setSafeToRedraw(true);
        }
      }
    }
  }

  private handlePostCompositionJointProcessing(wallId: string, scene?: THREE.Scene): void {
    if (this.jointProcessingDepth > 0) {
      return;
    }

    const wall = getFloorplannerStore().wallsMap.get(wallId);
    if (!wall) {
      return;
    }

    let joints = this.getJointsForWall(wallId);
    if (joints.length === 0) {
      joints = this.detectAndStoreJointsForWall(wallId);
    } else {
      joints.forEach(joint => jointCoordinator.markJointDirty(joint.id));
    }

    const processedJointIds = new Set<string>();
    const jointsNeedingProcessing = joints.filter(joint => {
      if (!this.shouldUseCoordinatedProcessing(joint)) {
        return false;
      }
      if (processedJointIds.has(joint.id)) {
        return false;
      }
      processedJointIds.add(joint.id);
      return true;
    });

    // Even if no joints need coordinated processing, we still need to run normal trimming
    // for L-joints and walls with endpoint connections
    if (jointsNeedingProcessing.length > 0) {
      jointsNeedingProcessing.forEach(joint => jointCoordinator.markJointDirty(joint.id));
      // For T/X/MULTI joints, coordinated processing happens in processJoint
      // which will eventually call trimShapes
    } else {
      // For L-joints, single walls, or walls without detected joints,
      // we still need to run normal trimming
      this.trimShapes(wall, scene);
    }
  }

  composeAllShapes = () => {
    // Set reconstruction flag for batch operation
    this.setIsReconstructingShapes(true);
    this.setSafeToRedraw(false);

    try {
      const walls = Array.from(getFloorplannerStore().wallsMap.values());
      const wallIds = walls.map(wall => wall.id);

      this.jointsMap.clear();
      this.clearJointPolygons();
      this.clearOutlineSegments();
      jointDetector.invalidateCache();
      jointCoordinator.clearAll();

      walls.forEach(wall => {
        // Use suppressFlags=true to avoid redundant flag toggles for individual walls
        this.composeWallShape(wall.id, undefined, true);
      });

      wallIds.forEach(wallId => {
        this.detectAndStoreJointsForWall(wallId);
      });

      wallIds.forEach(wallId => {
        this.handlePostCompositionJointProcessing(wallId);
      });

      wallIds.forEach(wallId => {
        this.generateJointPolygons(wallId);
        this.generateOutlineSegments(wallId);
      });
    } catch (error) {
      console.error('Error composing all shapes:', error);
      throw error;
    } finally {
      // Always clear reconstruction flag after all shapes are complete
      this.setIsReconstructingShapes(false);
      if (this.deferredUpdateDepth === 0) {
        this.setSafeToRedraw(true);
      }
    }
  }

  shapeChanged = (shape: WallShapeType, wall: WallType): boolean => {
    return WallChangeDetector.shapeChanged(shape, wall);
  }

  updateShapes = (wallId: string, scene?: THREE.Scene, geometryCache?: WallGeometryCache): WallShapeType[] => {
    // Use the new wall update manager for better performance
    const manager = this.getWallUpdateManager();
    if (manager) {
      manager.updateWall(wallId);
    } else {
      // Fallback to legacy method if manager not available yet
      return this.updateShapesLegacy(wallId, scene, geometryCache);
    }

    // Return the current shapes (will be updated asynchronously)
    const innerShapes = this.innerWallShapesMap.get(wallId);
    return innerShapes ?? [];
  };

  /**
   * Lazy initialization of wall update manager to avoid circular dependency
   */
  private getWallUpdateManager() {
    if (!this.wallUpdateManagerInstance) {
      import('../utils/wallUpdateManager').then(({ wallUpdateManager }) => {
        this.wallUpdateManagerInstance = wallUpdateManager;
        wallUpdateManager.initialize();
      });
    }
    return this.wallUpdateManagerInstance;
  }

  private wallUpdateManagerInstance: any = null;

  // Legacy method for backward compatibility
  updateShapesLegacy = (wallId: string, scene?: THREE.Scene, geometryCache?: WallGeometryCache): WallShapeType[] => {
    let innerShapes = this.innerWallShapesMap.get(wallId);
    if (innerShapes) {
      const wall = getFloorplannerStore().wallsMap.get(wallId);
      if (!wall) return [];
      innerShapes.forEach(innerShape => {
        if (this.shapeChanged(innerShape, wall)) {
          this.composeWallShape(wallId);
          const connectingWalls = this.getConnectedWallsList(wall);
          connectingWalls.forEach(w => {
            // Skip trimming for walls in progressive mode
            if (!this.getWallUpdateManager()?.isWallInProgressiveMode(w.id)) {
              this.trimShapes(w, scene, geometryCache);
            }
          });
          // Skip trimming if the wall is in progressive mode
          if (!this.getWallUpdateManager()?.isWallInProgressiveMode(wallId)) {
            this.trimShapes(wall, scene, geometryCache);
          }
        }
      })
    } else {
      const wall = getFloorplannerStore().wallsMap.get(wallId);
      if (!wall) return [];
      this.composeWallShape(wallId);

      // Skip trimming if the wall is in progressive mode
      if (!this.getWallUpdateManager()?.isWallInProgressiveMode(wallId)) {
        this.trimShapes(wall, scene, geometryCache);
      }

      // Skip the deep nested trimming (2 levels) when in progressive mode to prevent cascading updates
      if (!this.isAnyWallInProgressiveMode()) {
        // We have to trim the connecting walls as well, deeply nested at least 2 levels
        const connectingWalls = this.getConnectedWallsList(wall);
        connectingWalls.forEach(w => {
          this.trimShapes(w, scene, geometryCache);
          const connectingWalls2 = this.getConnectedWallsList(w);
          connectingWalls2.forEach(w2 => {
            this.trimShapes(w2, scene, geometryCache);
          });
        });
      } else {
        // During progressive mode, only perform basic shape composition without trimming
        // for connecting walls to avoid cascading performance issues
        const connectingWalls = this.getConnectedWallsList(wall);
        connectingWalls.forEach(w => {
          if (!this.getWallUpdateManager()?.isWallInProgressiveMode(w.id)) {
            this.trimShapes(w, scene, geometryCache);
          }
        });
      }
    }

    innerShapes = this.innerWallShapesMap.get(wallId);
    return innerShapes ?? [];
  };

  /**
   * Trims fill and outline geometry for a wall to ensure intersections and joints
   * are resolved. Passing `options.force` allows trimming even when the wall is
   * marked for progressive mode, which is useful for immediate rebuilds during
   * detach operations.
   */
  trimShapes = (wall: WallType, scene?: THREE.Scene, geometryCache?: WallGeometryCache, options?: TrimShapesOptions) => {
    const forceTrim = options?.force ?? false;

    // Skip all trimming operations if the wall is in progressive mode unless forcing
    // This prevents expensive polygon intersections and line trimming during drag
    if (!forceTrim && this.getWallUpdateManager()?.isWallInProgressiveMode(wall.id)) {
      // Trimming operations are deferred during drag for performance
      // Full geometry calculations will be performed when progressive mode ends
      return;
    }

    // Check if this wall participates in any multi-wall joints that need coordinated processing
    const joints = this.getJointsForWall(wall.id);
    const multiWallJoints = joints.filter(joint => this.shouldUseCoordinatedProcessing(joint));

    if (multiWallJoints.length > 0) {
      // This wall participates in T, X, or MULTI joints - use coordinated processing


      // Process each joint that this wall participates in
      // Use a Set to track processed joints to avoid duplicate processing
      const processedJointIds = new Set<string>();

      for (const joint of multiWallJoints) {
        // Skip if we've already processed this joint
        if (processedJointIds.has(joint.id)) {

          continue;
        }

        // Skip if joint is not dirty (already processed in this update cycle)
        if (!jointCoordinator.isJointDirty(joint.id)) {

          processedJointIds.add(joint.id);
          continue;
        }

        // Attempt coordinated processing
        const success = this.processJoint(joint, scene);

        if (success) {
          // Mark joint as processed
          processedJointIds.add(joint.id);

        } else {
          // Fall back to normal trimming for this wall

          // Don't mark as processed - allow retry or fallback
          break; // Exit loop and fall through to normal trimming below
        }
      }

      // If all joints were processed successfully, check if we need mid-point trimming
      if (processedJointIds.size === multiWallJoints.length) {
        // Still need to run mid-point trimming for symbols and mid-point connections
        // Fall through to normal trimming for mid-point connections
      }

      // Otherwise, fall through to normal trimming as fallback

    }

    // Normal trimming (original logic) - used for L/SINGLE joints or as fallback
    const otherModifiedPaths = [] as otherModifiedPath[];
    const innerShapes = this.innerWallShapesMap.get(wall.id);
    const outline1Shapes = this.outline1WallShapesMap.get(wall.id);
    const outline2Shapes = this.outline2WallShapesMap.get(wall.id);
    if (!innerShapes || !outline1Shapes || !outline2Shapes) {
      return;
    }

    // Walls are always 4-point rectangles now, so never have multi-joint geometry
    // This check is kept for safety but should never trigger
    const hasMultiJointGeometry = innerShapes.some(shape => shape.paths.length >= 5);

    if (hasMultiJointGeometry) {
      // This wall has multi-joint geometry - DO NOT run trimming
      // This should never happen with the current architecture
      if (this.debugLogging || isFloorplannerDebugLoggingEnabled()) {
        console.warn(`Skipping trimming for wall ${wall.id} - has multi-joint geometry`);
      }
      return;
    }

    // Add the new wall clippings and update the wall shapes on their intersections
    // Endpoint connections are handled by joint polygons, so only mid-point connections need trimming
    const connectedShapes = this.getConnectedShapes(wall.id, scene, geometryCache);
    const trimmed1 = trimIntersectingLines(outline1Shapes, connectedShapes, geometryCache);
    const trimmed2 = trimIntersectingLines(outline2Shapes, connectedShapes, geometryCache);
    const trimmed3 = trimIntersectingShapes(innerShapes, connectedShapes, otherModifiedPaths, scene, geometryCache);

    //if (trimmed1 || trimmed2 || trimmed3) {

    if (innerShapes && outline1Shapes && outline2Shapes) {
      this.outline1WallShapesMap.set(wall.id, outline1Shapes);
      this.outline2WallShapesMap.set(wall.id, outline2Shapes);
      this.innerWallShapesMap.set(wall.id, innerShapes);
      otherModifiedPaths.forEach(mods => {
        //console.log("mods: ", mods, wall.id, wall.fillColor);
        const wallShapes = this.innerWallShapesMap.get(mods.wallId);
        if (wallShapes) {
          const wallShape = wallShapes.find(ws => ws.lineSide === "inner");
          if (wallShape) {
            updatePathPoint(wallShape, mods.pathIndex, mods.point);
            this.innerWallShapesMap.set(mods.wallId, wallShapes);
            getFloorplannerStore().updateWallCornerStateFromShape(wallShape);
            // Debug draw a dot at mods.point
            // const geometry1 = new THREE.CircleGeometry(0.01, 32);
            // const material1 = new THREE.MeshBasicMaterial({ color: wallShape.wall.fillColor });
            // const circle1 = new THREE.Mesh(geometry1, material1);
            // circle1.position.set(mods.point.x, mods.point.y, 0.01);
            // scene?.add(circle1);
          }
        }
      });
    }
  }

  clearWallShapes = (wallId: string) => {
    if (wallId) {
      this.innerWallShapesMap.set(wallId, []);
      this.innerWallShapesMap.delete(wallId);
      this.outline1WallShapesMap.set(wallId, []);
      this.outline1WallShapesMap.delete(wallId);
      this.outline2WallShapesMap.set(wallId, []);
      this.outline2WallShapesMap.delete(wallId);
      this.endCapsWallShapesMap.set(wallId, []);
      this.endCapsWallShapesMap.delete(wallId);
      // Clear joint polygons for this wall
      const joints = this.getJointsForWall(wallId);
      joints.forEach(joint => this.clearJointPolygons(joint.id));
      // Clear outline segments for this wall (but skip during drag to prevent visual flicker)
      if (this.deferredUpdateDepth === 0) {
        this.clearOutlineSegments(wallId);
      }
    } else {
      this.innerWallShapesMap.clear();
      this.outline1WallShapesMap.clear();
      this.outline2WallShapesMap.clear();
      this.endCapsWallShapesMap.clear();
      // Clear all joint polygons
      this.clearJointPolygons();
      // Clear all outline segments
      this.clearOutlineSegments();
    }
  }

  /**
   * Detect and store joints for a specific wall
   * This should be called after wall geometry is composed but before trimming
   */
  detectAndStoreJointsForWall = (wallId: string): JointInfo[] => {
    const wall = getFloorplannerStore().wallsMap.get(wallId);
    if (!wall) return [];

    const detectedJoints: JointInfo[] = [];
    const seenJointIds = new Set<string>();

    const storeJoint = (joint: JointInfo) => {
      if (seenJointIds.has(joint.id)) {
        return;
      }

      this.jointsMap.set(joint.id, joint);
      detectedJoints.push(joint);
      seenJointIds.add(joint.id);
      // Mark joint as dirty in coordinator for future processing
      jointCoordinator.markJointDirty(joint.id);
    };

    // Use topology-based detection first. Edge-based joints can be visually
    // connected through rendered endpoint extensions even when the wall
    // centerline endpoints do not occupy the same coordinate.
    jointDetector.getJointsForWall(wallId).forEach(storeJoint);

    // Detect joint at wall start point
    const startJoint = jointDetector.detectJointAtPoint(wall.start);
    if (startJoint) {
      storeJoint(startJoint);
    }

    // Detect joint at wall end point
    const endJoint = jointDetector.detectJointAtPoint(wall.end);
    if (endJoint) {
      // Only add if it's a different joint than the start joint
      if (!startJoint || endJoint.id !== startJoint.id) {
        storeJoint(endJoint);
      }
    }

    return detectedJoints;
  }

  /**
   * Get all joints that a specific wall participates in
   * Returns joints from the stored map
   */
  getJointsForWall = (wallId: string): JointInfo[] => {
    const joints: JointInfo[] = [];

    // Iterate through all stored joints and find ones that include this wall
    this.jointsMap.forEach(joint => {
      if (joint.connectedWalls.some(conn => conn.wallId === wallId)) {
        joints.push(joint);
      }
    });

    return joints;
  }

  /**
   * Generate joint polygons for a specific wall
   * Creates gap-filling polygons for edge-connected walls
   *
   * TRIGGERED BY:
   * - Wall position changes
   * - Wall connections updated
   * - New joint detected
   *
   * CACHING STRATEGY:
   * Joint polygons are cached in jointPolygonsMap
   * Cache key is joint ID
   * Invalidated when any participating wall changes
   */
  @action
  generateJointPolygons = (wallId: string): void => {
    const triggerReason = (this as any)._lastJointGenerationTrigger?.[wallId] || 'unknown';
    if (this.debugLogging) {
      console.group(`🔷 generateJointPolygons - Wall ${wallId}`);
      console.log(`  Trigger reason: ${triggerReason}`);
    }

    // Get all joints that this wall participates in
    // Joints are detected by JointDetector and stored in jointsMap
    const joints = this.getJointsForWall(wallId);
    if (this.debugLogging) {
      console.log(`  Found ${joints.length} joint(s) for this wall`);
    }

    // First, check if any joint is a multi-joint (3+ walls)
    // If so, we should only render the multi-joint, not individual 2-wall voidFill polygons
    const hasMultiJoint = joints.some(j => j.connectedWalls.length >= 3);

    // Generate a joint polygon for each joint
    // Each polygon fills the gap between connected wall edges
    for (const joint of joints) {
      if (this.debugLogging) {
        console.log(`  Processing joint ${joint.id} (type: ${joint.jointType}, connectedWalls: ${joint.connectedWalls.length})`);
        // Log which walls are affected by this joint
        const wallIds = joint.connectedWalls.map(cw => cw.wallId);
        console.log(`    Joint ${joint.id} affects walls: ${wallIds.join(', ')}`);
      }

      // Skip 2-wall voidFill polygons if this wall is part of a multi-joint
      // The multi-joint polygon will handle all filling
      if (hasMultiJoint && joint.connectedWalls.length < 3 && joint.voidFillMetadata) {
        if (this.debugLogging) {
          console.log(`    Skipping joint ${joint.id}: 2-wall voidFill, but wall is part of multi-joint`);
        }
        continue;
      }

      // Skip joint polygon generation for simple straight connections (no corner fill needed)
      // Generate polygons for:
      // - T, X, MULTI joints
      // - Joints with explicit voidFillMetadata
      // - L-joints (2-wall corners) - these always need corner fills even if voidFillMetadata isn't set yet
      const isLJoint = joint.jointType === 'L' && joint.connectedWalls.length === 2;
      const isMultiWallJoint = joint.connectedWalls.length >= 3 || this.shouldUseCoordinatedProcessing(joint);
      if (!isMultiWallJoint && !joint.needsSpecialHandling && !joint.voidFillMetadata && !isLJoint) {
        if (this.debugLogging) {
          console.log(`    Skipping joint ${joint.id}: simple connection, no special handling needed`);
        }
        continue;
      }

      // Check cache first
      const cachedJointPolygon = wallGeometryCache.getJointPolygon(joint.id, getFloorplannerStore());
      let jointPolygon: JointPolygonData | null = null;

      if (cachedJointPolygon) {
        // Cache hit - extract polygonData from cached object
        this.jointPolygonCacheHits++;
        jointPolygon = cachedJointPolygon.polygonData;
        if (this.debugLogging) {
          console.log(`    Joint polygon cache HIT for joint ${joint.id}`);
        }
      } else {
        // Cache miss - generate new polygon
        this.jointPolygonCacheMisses++;
        if (this.debugLogging) {
          console.log(`    Joint polygon cache MISS for joint ${joint.id}, generating...`);
        }

        // Track generation time for performance monitoring
        const generationStart = performance.now();
        jointPolygon = createJointPolygon(joint, getFloorplannerStore().wallsMap);
        const generationTime = performance.now() - generationStart;

        if (generationTime > 10 && (this.debugLogging || isFloorplannerDebugLoggingEnabled())) {
          console.warn(`  Slow joint polygon generation for joint ${joint.id}: ${generationTime.toFixed(2)}ms`);
        }
      }

      // Store polygon if successfully created
      // Returns null if validation fails or polygon not needed
      if (jointPolygon) {
        if (this.debugLogging) {
          console.log(`    Joint polygon ${cachedJointPolygon ? '(cached)' : 'created successfully'}:`, {
            jointId: joint.id,
            vertices: jointPolygon.vertices.length,
            fillColor: `#${jointPolygon.fillColor.toString(16)}`,
            zIndex: jointPolygon.zIndex,
          });
        }

        // Cache joint polygon in WallGeometryCache (single source of truth) if not already cached
        if (!cachedJointPolygon) {
          // Extract participating wall IDs from the joint
          const wallIds = joint.connectedWalls.map(w => w.wallId);
          wallGeometryCache.setJointPolygon(joint.id, jointPolygon, wallIds);
        }

        // Also store in renderStore for immediate access by components
        // The cache will be the authoritative source, but renderStore provides
        // the runtime data for rendering
        this.jointPolygonsMap.set(joint.id, jointPolygon);
      } else if (this.debugLogging) {
        console.log(`    No joint polygon needed for joint ${joint.id}`);
      }
    }
    if (this.debugLogging) {
      console.groupEnd();
    }

    // Log cache statistics periodically when debug logging is active
    if (this.debugLogging) {
      // Log when misses reach a milestone (every 100 misses)
      if (this.jointPolygonCacheMisses > 0 && this.jointPolygonCacheMisses % 100 === 0) {
        this.logJointPolygonCacheStats();
      }
    }
  }

  /**
   * Log joint polygon cache statistics
   * Computes and logs hit rate, total requests, and cache efficiency metrics
   * Only logs when debugLogging is true
   */
  logJointPolygonCacheStats(): void {
    if (!this.debugLogging) {
      return;
    }

    const totalRequests = this.jointPolygonCacheHits + this.jointPolygonCacheMisses;
    if (totalRequests === 0) {
      return;
    }

    const hitRate = (this.jointPolygonCacheHits / totalRequests * 100).toFixed(1);
    console.log('Joint polygon cache stats:', {
      hits: this.jointPolygonCacheHits,
      misses: this.jointPolygonCacheMisses,
      hitRate: `${hitRate}%`,
      totalRequests
    });
  }

  /**
   * Reset joint polygon cache statistics
   * Called when clearing joint polygons or on project load for session-based tracking
   */
  @action
  resetJointPolygonCacheStats(): void {
    this.jointPolygonCacheHits = 0;
    this.jointPolygonCacheMisses = 0;
  }

  /**
   * Get joint polygons for a specific wall
   * Returns all joint polygons that a wall participates in
   */
  getJointPolygonsForWall = (wallId: string): JointPolygonData[] => {
    const joints = this.getJointsForWall(wallId);
    const jointPolygons: JointPolygonData[] = [];

    for (const joint of joints) {
      const jointPolygon = this.jointPolygonsMap.get(joint.id);
      if (jointPolygon) {
        jointPolygons.push(jointPolygon);
      }
    }

    return jointPolygons;
  }

  /**
   * Clear joint polygons for a specific joint or all joints
   * If jointId is provided, only that joint's polygon is cleared
   * Otherwise, all joint polygons are cleared
   */
  @action
  clearJointPolygons = (jointId?: string): void => {
    if (jointId) {
      this.jointPolygonsMap.delete(jointId);
      // Also clear from WallGeometryCache
      wallGeometryCache.clearJoint(jointId);
    } else {
      this.jointPolygonsMap.clear();
      wallGeometryCache.clearAllJointPolygons();
      // Reset cache statistics when clearing all joint polygons
      this.resetJointPolygonCacheStats();
    }
  }

  @action
  clearJointPolygonsForWall = (wallId: string): Set<string> => {
    const affectedWalls = new Set<string>();
    const jointIdsToClear: string[] = [];

    this.jointPolygonsMap.forEach((jointPolygon, jointId) => {
      if (!jointPolygon.wallIds.includes(wallId)) {
        return;
      }

      jointIdsToClear.push(jointId);
      jointPolygon.wallIds.forEach(id => affectedWalls.add(id));
    });

    jointIdsToClear.forEach(jointId => this.clearJointPolygons(jointId));
    wallGeometryCache.clearJointPolygonsForWall(wallId);

    return affectedWalls;
  }

  /**
   * Invalidate joints for a specific wall
   * This should be called when a wall's connections or position changes
   */
  invalidateJointsForWall = (
    wallId: string,
    reason: string = 'unknown',
    options: { scheduleAffectedWallUpdates?: boolean } = {}
  ): void => {
    const { scheduleAffectedWallUpdates = true } = options;

    // Always scan jointsMap to remove joints containing wallId, even if the wall is missing
    // This prevents stale joints from remaining when walls are deleted
    const jointsToRemove: string[] = [];
    const affectedWalls = new Set<string>();

    this.clearJointPolygonsForWall(wallId).forEach(affectedWallId => {
      affectedWalls.add(affectedWallId);
    });

    this.jointsMap.forEach((joint, jointId) => {
      if (joint.connectedWalls.some(conn => conn.wallId === wallId)) {
        jointsToRemove.push(jointId);
        // Collect all walls affected by this joint
        joint.connectedWalls.forEach(cw => affectedWalls.add(cw.wallId));
        // Mark joint as dirty in coordinator
        jointCoordinator.markJointDirty(jointId);
        // Clear multi-joint renderer cache for this joint
        multiJointRenderer.clearJointCache(jointId);
        // Clear joint polygon for this joint
        this.clearJointPolygons(jointId);
      }
    });

    if (this.debugLogging && jointsToRemove.length > 0) {
      console.log(`  Invalidating joints: ${jointsToRemove.join(', ')}`);
      console.log(`  Reason: ${reason}`);
      console.log(`  Affected walls: ${Array.from(affectedWalls).join(', ')}`);
    }

    // Remove the joints from the map
    jointsToRemove.forEach(jointId => {
      this.jointsMap.delete(jointId);
    });

    // Also invalidate the joint detector's cache
    jointDetector.invalidateCache();

    // CRITICAL: Clear outline segments for affected walls AND trigger redraw
    // This ensures endcaps are redrawn when walls disconnect from joints
    // However, skip triggering objectNeedsUpdate for walls in progressive mode
    // to prevent expensive cascading updates during wall construction/drag
    const wallUpdateManager = this.getWallUpdateManager();
    affectedWalls.forEach(affectedWallId => {
      this.clearOutlineSegments(affectedWallId);
      // Also clear from geometry cache to force regeneration
      wallGeometryCache.clearOutlineSegmentsForWall(affectedWallId);
      // Only trigger update for walls NOT in progressive mode. Callers that
      // already own wall-update-manager processing pass scheduleAffectedWallUpdates=false
      // to avoid recursively queuing another full dependency update.
      if (scheduleAffectedWallUpdates && !wallUpdateManager?.isWallInProgressiveMode(affectedWallId)) {
        getFloorplannerStore().objectNeedsUpdate(affectedWallId);
      }
    });

    // Cascade invalidation to connected walls
    if (affectedWalls.size > 1) {
      const connectedWallIds = Array.from(affectedWalls).filter(id => id !== wallId);
      if (this.debugLogging && connectedWallIds.length > 0) {
        console.log(`  Invalidating connected walls: ${connectedWallIds.join(', ')}`);
      }
    }
  }

  /**
   * Rebuild final wall and joint render data for a settled connection topology.
   *
   * Drag/drop can enqueue wall updates while connection mutations are still in
   * flight. This method is intentionally synchronous and topology-first so the
   * last redraw after a disconnect uses the current wall graph, not an older
   * progressive/cache snapshot.
   */
  @action
  finalizeJointGeometryForWalls = (
    wallIds: Iterable<string>,
    reason: string = 'unknown',
    scene?: THREE.Scene,
  ): void => {
    const store = getFloorplannerStore();
    const affectedWallIds = new Set<string>();

    for (const wallId of wallIds) {
      if (!wallId || !store.wallsMap.has(wallId)) {
        continue;
      }
      affectedWallIds.add(wallId);
    }

    // Include current direct neighbors so a multi-joint that becomes a 2-wall
    // joint is rebuilt using the remaining pair as a single topology update.
    Array.from(affectedWallIds).forEach(wallId => {
      const wall = store.wallsMap.get(wallId);
      if (!wall) {
        return;
      }
      wall.edgeConnections?.forEach(connection => {
        if (store.wallsMap.has(connection.targetWallId)) {
          affectedWallIds.add(connection.targetWallId);
        }
      });
      wall.centerConnections?.forEach(connection => {
        if (store.wallsMap.has(connection.id)) {
          affectedWallIds.add(connection.id);
        }
      });
    });

    if (affectedWallIds.size === 0) {
      return;
    }

    const staleJointIds = new Set<string>();
    this.jointsMap.forEach((joint, jointId) => {
      const touchesAffectedWall = joint.connectedWalls.some(connection =>
        affectedWallIds.has(connection.wallId)
      );
      if (!touchesAffectedWall) {
        return;
      }

      staleJointIds.add(jointId);
      joint.connectedWalls.forEach(connection => {
        if (store.wallsMap.has(connection.wallId)) {
          affectedWallIds.add(connection.wallId);
        }
      });
    });

    affectedWallIds.forEach(wallId => {
      this.clearJointPolygonsForWall(wallId);
      this.clearOutlineSegments(wallId);
      wallGeometryCache.clearWall(wallId);
    });

    staleJointIds.forEach(jointId => {
      this.jointsMap.delete(jointId);
      this.clearJointPolygons(jointId);
      jointCoordinator.markJointDirty(jointId);
      multiJointRenderer.clearJointCache(jointId);
    });

    jointDetector.invalidateCache();

    const affectedWalls = Array.from(affectedWallIds)
      .map(wallId => store.wallsMap.get(wallId))
      .filter((wall): wall is WallType => !!wall);

    affectedWalls.forEach(wall => {
      ensureWallHasVersion(wall);
      wall.geometryVersion = (wall.geometryVersion || 0) + 1;
      wall.lastUpdate = Date.now();
      this.clearWallShapes(wall.id);
      this.composeWallShape(wall.id, scene, true);
    });

    affectedWalls.forEach(wall => {
      this.detectAndStoreJointsForWall(wall.id);
    });

    affectedWalls.forEach(wall => {
      this.handlePostCompositionJointProcessing(wall.id, scene);
    });

    affectedWalls.forEach(wall => {
      this.generateJointPolygons(wall.id);
      this.generateOutlineSegments(wall.id);
      store.wallsMap.set(wall.id, wall);
      this.requestInvalidation(wall.id);
    });

    if (this.debugLogging) {
      console.log(`finalizeJointGeometryForWalls(${reason})`, {
        wallIds: Array.from(affectedWallIds),
        staleJointIds: Array.from(staleJointIds),
      });
    }
  }

  @action
  refreshWallWidthGeometry = (
    wallIds: Iterable<string>,
    reason: string = 'wallWidth_change',
    scene?: THREE.Scene,
  ): void => {
    const manager = this.getWallUpdateManager();
    manager?.clearProgressiveModeIfIdle?.(reason);

    const store = getFloorplannerStore();
    const affectedWallIds = new Set<string>();

    const addWallAndDirectConnections = (wallId: string): void => {
      const wall = store.wallsMap.get(wallId);
      if (!wall) {
        return;
      }

      affectedWallIds.add(wallId);
      wall.centerConnections?.forEach(connection => {
        if (store.wallsMap.has(connection.id)) {
          affectedWallIds.add(connection.id);
        }
      });
      wall.edgeConnections?.forEach(connection => {
        if (store.wallsMap.has(connection.targetWallId)) {
          affectedWallIds.add(connection.targetWallId);
        }
      });
    };

    for (const wallId of wallIds) {
      addWallAndDirectConnections(wallId);
    }

    Array.from(affectedWallIds).forEach(addWallAndDirectConnections);

    if (affectedWallIds.size === 0) {
      return;
    }

    const localJoints = jointDetector.getJointsForWallsLocal(affectedWallIds);
    const localJointIds = new Set(localJoints.map(joint => joint.id));

    localJoints.forEach(joint => {
      this.jointsMap.set(joint.id, joint);
      jointCoordinator.markJointDirty(joint.id);
      wallGeometryCache.registerJointFromInfo(joint);
    });

    localJointIds.forEach(jointId => {
      this.clearJointPolygons(jointId);
      multiJointRenderer.clearJointCache(jointId);
    });

    affectedWallIds.forEach(wallId => {
      this.clearOutlineSegments(wallId);
      wallGeometryCache.clearSimplifiedGeometry(wallId);
    });

    const now = Date.now();
    const affectedWalls = Array.from(affectedWallIds)
      .map(wallId => store.wallsMap.get(wallId))
      .filter((wall): wall is WallType => !!wall);

    affectedWalls.forEach(wall => {
      ensureWallHasVersion(wall);
      wall.geometryVersion = (wall.geometryVersion || 0) + 1;
      wall.lastUpdate = now;
      this.composeWallShape(wall.id, scene, true);
    });

    affectedWalls.forEach(wall => {
      this.generateJointPolygons(wall.id);
      this.generateOutlineSegments(wall.id);
      store.wallsMap.set(wall.id, wall);
    });

    this.requestInvalidation(affectedWalls[0]?.id);

    if (this.debugLogging) {
      console.log(`refreshWallWidthGeometry(${reason})`, {
        wallIds: Array.from(affectedWallIds),
        jointIds: Array.from(localJointIds),
      });
    }
  }

  updateJointFillStyleForWall = (
    wallId: string,
    fillColor: number,
    fillPattern?: FillPattern,
    fillOpacity?: number,
  ): void => {
    let didUpdate = false;

    this.jointPolygonsMap.forEach((jointPolygon, jointId) => {
      const primaryWallId = jointPolygon.primaryWallId ?? (jointPolygon.isVoidFill
        ? jointPolygon.voidFillMetadata?.ownerWallId ?? jointPolygon.wallIds[0]
        : jointPolygon.wallIds[0]);

      if (primaryWallId !== wallId) {
        return;
      }

      if (
        jointPolygon.fillColor !== fillColor ||
        jointPolygon.fillPattern !== fillPattern ||
        jointPolygon.fillOpacity !== fillOpacity
      ) {
        const updatedJointPolygon = {
          ...jointPolygon,
          fillColor,
          fillPattern,
          fillOpacity,
          voidFillMetadata: jointPolygon.voidFillMetadata
            ? {
              ...jointPolygon.voidFillMetadata,
              fillColor,
            }
            : jointPolygon.voidFillMetadata,
        };
        this.jointPolygonsMap.set(jointId, updatedJointPolygon);
        wallGeometryCache.setJointPolygon(jointId, updatedJointPolygon, [...updatedJointPolygon.wallIds]);
        didUpdate = true;
      }

      const joint = this.jointsMap.get(jointId);
      if (joint?.voidFillMetadata?.ownerWallId === wallId && joint.voidFillMetadata.fillColor !== fillColor) {
        this.jointsMap.set(jointId, {
          ...joint,
          voidFillMetadata: {
            ...joint.voidFillMetadata,
            fillColor,
          },
        });
      }
    });

    if (didUpdate) {
      invalidate();
    }
  }

  updateJointLineStyleForWall = (
    wallId: string,
    lineColor: number,
    lineWeight: number,
    lineType: LineType = LineType.solid,
  ): void => {
    let didUpdate = false;
    const outlineDashed = lineType === LineType.dashed;

    this.jointPolygonsMap.forEach((jointPolygon, jointId) => {
      if (!jointPolygon.wallIds.includes(wallId)) {
        return;
      }

      const primaryWallId = jointPolygon.primaryWallId ?? (jointPolygon.isVoidFill
        ? jointPolygon.voidFillMetadata?.ownerWallId ?? jointPolygon.wallIds[0]
        : jointPolygon.wallIds[0]);
      const ownsJointStyle = primaryWallId === wallId;
      let segmentsChanged = false;

      const outlineSegments = jointPolygon.outlineSegments?.map(segment => {
        if (segment.wallId !== wallId && !(ownsJointStyle && !segment.wallId)) {
          return segment;
        }
        if (
          segment.color === lineColor &&
          segment.width === lineWeight &&
          segment.dashed === outlineDashed
        ) {
          return segment;
        }
        segmentsChanged = true;
        return {
          ...segment,
          color: lineColor,
          width: lineWeight,
          dashed: outlineDashed,
        };
      });

      const nextOutlineColor = ownsJointStyle ? lineColor : jointPolygon.outlineColor;
      const nextOutlineWidth = ownsJointStyle ? lineWeight : jointPolygon.outlineWidth;
      const nextOutlineDashed = ownsJointStyle ? outlineDashed : jointPolygon.outlineDashed;
      const nextVoidFillMetadata =
        jointPolygon.voidFillMetadata && jointPolygon.voidFillMetadata.ownerWallId === wallId
          ? {
            ...jointPolygon.voidFillMetadata,
            outlineColor: lineColor,
            outlineWidth: lineWeight,
            outlineDashed,
          }
          : jointPolygon.voidFillMetadata;

      const styleChanged =
        jointPolygon.outlineColor !== nextOutlineColor ||
        jointPolygon.outlineWidth !== nextOutlineWidth ||
        jointPolygon.outlineDashed !== nextOutlineDashed ||
        jointPolygon.voidFillMetadata !== nextVoidFillMetadata;

      if (segmentsChanged || styleChanged) {
        const updatedJointPolygon: JointPolygonData = {
          ...jointPolygon,
          outlineColor: nextOutlineColor,
          outlineWidth: nextOutlineWidth,
          outlineDashed: nextOutlineDashed,
          outlineSegments,
          voidFillMetadata: nextVoidFillMetadata,
        };
        this.jointPolygonsMap.set(jointId, updatedJointPolygon);
        wallGeometryCache.setJointPolygon(jointId, updatedJointPolygon, [...updatedJointPolygon.wallIds]);
        didUpdate = true;
      }

      const joint = this.jointsMap.get(jointId);
      if (joint?.voidFillMetadata?.ownerWallId === wallId) {
        this.jointsMap.set(jointId, {
          ...joint,
          voidFillMetadata: {
            ...joint.voidFillMetadata,
            outlineColor: lineColor,
            outlineWidth: lineWeight,
            outlineDashed,
          },
        });
      }
    });

    if (didUpdate) {
      invalidate();
    }
  }

  /**
   * Clear all stored joints
   * This should be called when the floorplan is cleared or reset
   */
  clearAllJoints = (): void => {
    this.jointsMap.clear();
    jointDetector.fullReset(); // Use fullReset for complete clearing
    jointCoordinator.clearAll();
    multiJointRenderer.clearAllCaches(); // Clear multi-joint renderer cache
  }

  /**
   * Generate outline segments for a specific wall
   * Creates segmented outline lines with gaps at connection points
   *
   * TRIGGERED BY:
   * - Wall position changes
   * - Wall width/color changes
   * - Wall connections updated
   * - Cutouts added/removed
   *
   * CACHING STRATEGY:
   * Segments are cached in outlineSegmentsMap
   * Cache key is wall ID
   * Invalidated when wall geometry or properties change
   *
   * OUTLINE SEGMENTATION:
   * Instead of filled rectangles with clipping, we create segmented lines:
   * 1. Calculate left/right outline positions
   * 2. Split at edge connections (0.02 unit gap)
   * 3. Split at mid-point connections (proportional gap)
   * 4. Split at cutouts (door/window openings)
   * 5. Render as LineSegments2 with pre-calculated segments
   */
  @action
  generateOutlineSegments = (wallId: string): void => {
    const triggerReason = (this as any)._lastOutlineGenerationTrigger?.[wallId] || 'unknown';
    if (this.debugLogging) {
      console.group(`📏 generateOutlineSegments - Wall ${wallId}`);
      console.log(`  Trigger reason: ${triggerReason}`);
      console.log(`  Geometry version before: ${getFloorplannerStore().wallsMap.get(wallId)?.geometryVersion || 'unknown'}`);
    }

    // Get wall from floorplanner store
    const wall = getFloorplannerStore().wallsMap.get(wallId);
    if (!wall) {
      // Wall not found, nothing to generate
      if (this.debugLogging) {
        console.groupEnd();
      }
      return;
    }

    // Log wall state
    if (this.debugLogging) {
      console.log(`  Wall state:`, {
        position: `(${wall.start.x.toFixed(3)}, ${wall.start.y.toFixed(3)}) -> (${wall.end.x.toFixed(3)}, ${wall.end.y.toFixed(3)})`,
        width: wall.wallWidth || getFloorplannerStore().wallWidth,
        edgeConnections: (wall.edgeConnections || []).length,
        regularConnections: (wall.centerConnections || []).length,
        cutouts: (wall.cutouts || []).length,
      });
    }

    // GENERATE SEGMENTS:
    // Call outlineSegmentation.ts to create segmented outline geometry
    // This calculates all the splits and creates individual line segments
    const segments = createOutlineSegments(this.getWallForOutlineGeneration(wall));

    if (this.debugLogging) {
      console.log(`  Segments generated:`, {
        leftSegments: segments.leftSegments.length,
        rightSegments: segments.rightSegments.length,
        endCapSegments: segments.endCapSegments.length,
        total: segments.leftSegments.length + segments.rightSegments.length + segments.endCapSegments.length,
        geometryVersion: segments.geometryVersion,
      });
      console.log(`  Geometry version after: ${segments.geometryVersion}`);
    }

    // Cache outline segments in WallGeometryCache (single source of truth)
    wallGeometryCache.setOutlineSegments(wallId, segments);

    // Also store in renderStore for immediate access by components
    // The cache will be the authoritative source, but renderStore provides
    // the runtime data for rendering
    this.outlineSegmentsMap.set(wallId, segments);

    // Verify cache invalidation for both walls at connections
    if (this.debugLogging) {
      const edgeConnections = wall.edgeConnections || [];
      for (const conn of edgeConnections) {
        const otherWallId = conn.sourceWallId === wallId ? conn.targetWallId : conn.sourceWallId;
        const otherWallSegments = this.outlineSegmentsMap.get(otherWallId);
        console.log(`  Connection check: wall ${otherWallId} ${otherWallSegments ? 'has' : 'missing'} cached segments`);
      }
    }

    if (this.debugLogging) {
      console.log(`  Segments cached successfully`);
      console.groupEnd();
    }
  }

  /**
   * Get outline segments for a specific wall
   * Returns the segment collection for the wall
   */
  getOutlineSegments = (wallId: string): OutlineSegmentCollection | undefined => {
    const cached = this.outlineSegmentsMap.get(wallId);
    if (cached) return cached;

    // Lazy-generate if missing, but DEFER the actual generation to avoid
    // triggering setState during render (React warning).
    // We use a module-level Set to track pending generations to completely
    // avoid MobX reactivity during the render phase.
    const store = getFloorplannerStore();
    const wall = store.wallsMap.get(wallId);
    if (wall && !pendingOutlineGenerations.has(wallId)) {
      pendingOutlineGenerations.add(wallId);
      queueMicrotask(() => {
        pendingOutlineGenerations.delete(wallId);
        // Only generate if still missing (could have been generated by another path)
        if (!this.outlineSegmentsMap.has(wallId)) {
          // Use runInAction for the actual generation since it modifies observable state
          runInAction(() => {
            this.generateOutlineSegments(wallId);
          });
          // Request invalidation to trigger re-render with new segments
          this.requestInvalidation(wallId);
        }
      });
    }
    return undefined;
  }

  /**
   * Clear outline segments for a specific wall or all walls
   * If wallId is provided, only that wall's segments are cleared
   * Otherwise, all outline segments are cleared
   */
  @action
  clearOutlineSegments = (wallId?: string): void => {
    if (wallId) {
      this.outlineSegmentsMap.delete(wallId);
      // Also clear from WallGeometryCache
      wallGeometryCache.clearOutlineSegmentsForWall(wallId);
    } else {
      this.outlineSegmentsMap.clear();
      // Clear all outline segments from cache
      this.outlineSegmentsMap.forEach((_, id) => {
        wallGeometryCache.clearOutlineSegmentsForWall(id);
      });
    }
  }

  /**
   * Determine if a joint requires coordinated processing.
   * Only T, X, and MULTI joints need coordinated processing.
   * L and SINGLE joints work fine with standard trimming.
   */
  shouldUseCoordinatedProcessing(joint: JointInfo): boolean {
    return joint.jointType === 'T' || joint.jointType === 'X' || joint.jointType === 'MULTI';
  }

  /**
   * Process a multi-wall joint with coordinated trimming.
   * This solves the T-connection problem by processing walls in priority order
   * and using cached geometry so secondary walls trim against updated primary wall geometry.
   *
   * @param joint The joint to process
   * @param scene Optional Three.js scene for rendering
   * @returns true if processing succeeded, false if it should fall back to normal trimming
   */
  private processJoint(joint: JointInfo, scene?: THREE.Scene): boolean {
    const startTime = performance.now();
    const operationId = `processJoint_${joint.id}_${Date.now()}`;

    this.jointProcessingDepth++;
    try {
      // Log joint processing start


      // Register joint in cache for tracking
      wallGeometryCache.registerJointFromInfo(joint);

      // Get processing order from JointCoordinator
      const processingOrder = jointCoordinator.getProcessingOrder(joint);
      const primaryWall = jointCoordinator.getPrimaryWall(joint);



      // Phase 1: Compose and cache base geometry for all walls in processing order
      for (const wallId of processingOrder) {
        const wall = getFloorplannerStore().wallsMap.get(wallId);
        if (!wall) {

          continue;
        }

        // Skip if wall is in progressive mode
        if (this.getWallUpdateManager()?.isWallInProgressiveMode(wallId)) {

          continue;
        }

        try {
          // Compose base geometry (this updates renderStore maps)
          this.composeWallShape(wallId, scene, true); // suppressFlags=true for batch operation

          // Get the composed geometry from renderStore maps
          const inner = this.innerWallShapesMap.get(wallId) || [];
          const outline1 = this.outline1WallShapesMap.get(wallId) || [];
          const outline2 = this.outline2WallShapesMap.get(wallId) || [];
          const endcaps = this.endCapsWallShapesMap.get(wallId) || [];

          // Store in cache for other walls to query WITH version tracking
          wallGeometryCache.setWallGeometry(wallId, {
            inner,
            outline1,
            outline2,
            endcaps
          }, wall, getFloorplannerStore());


        } catch (composeError) {
          console.error('renderStore: Error composing geometry for wall in joint', {
            wallId,
            jointId: joint.id,
            error: composeError instanceof Error ? composeError.message : String(composeError)
          });
          PerformanceMonitor.trackError(wallId, 'processJoint', 'compose_error');
          // Continue processing other walls
        }
      }

      // Phase 2: Use multi-joint renderer for T, X, and MULTI joints
      try {
        let trimmingResult: any = null;

        // Use multi-joint renderer for complex joints
        if (joint.jointType === 'T' || joint.jointType === 'X' || joint.jointType === 'MULTI') {
          // Skip multi-joint rendering and rely on joint polygons
          // Multi-joint work is handled by joint polygon generation
          // Mark all walls as processed
          processingOrder.forEach(wallId => {
            jointCoordinator.markWallProcessed(joint.id, wallId);
          });
          trimmingResult = { success: true };
        }

        // Fallback to original trimming for mid-point connections:
        // - Endpoint connections: Handled by joint polygons
        // - Mid-point connections: Processed by trimIntersectingShapes()
        // - Edge connections preserve wall lengths (no extension needed for endpoints)
        // - Joint polygons fill gaps at endpoints (no trimming needed for endpoints)
        // - Outline segmentation handles outline gaps (no outline extension needed)
        if (!trimmingResult || !trimmingResult.success) {
          // Original trimming logic for mid-point connections
          for (const wallId of processingOrder) {
            const wall = getFloorplannerStore().wallsMap.get(wallId);
            if (!wall) continue;

            if (this.getWallUpdateManager()?.isWallInProgressiveMode(wallId)) {
              continue;
            }

            try {
              const inner = wallGeometryCache.getInnerShapes(wallId);
              const outline1 = wallGeometryCache.getOutline1Shapes(wallId);
              const outline2 = wallGeometryCache.getOutline2Shapes(wallId);

              if (!inner || !outline1 || !outline2) {
                continue;
              }

              const connectedShapes = this.getConnectedShapes(wallId, scene, wallGeometryCache);
              const otherModifiedPaths = [] as otherModifiedPath[];

              trimIntersectingLines(outline1, connectedShapes, wallGeometryCache);
              trimIntersectingLines(outline2, connectedShapes, wallGeometryCache);
              trimIntersectingShapes(inner, connectedShapes, otherModifiedPaths, scene, wallGeometryCache);

              wallGeometryCache.setWallGeometry(wallId, { inner, outline1, outline2 }, wall, getFloorplannerStore());
              jointCoordinator.markWallProcessed(joint.id, wallId);

            } catch (trimError) {
              console.error('renderStore: Error in fallback trimming', {
                wallId,
                jointId: joint.id,
                error: trimError instanceof Error ? trimError.message : String(trimError)
              });
              PerformanceMonitor.trackError(wallId, 'processJoint', 'fallback_trim_error');
            }
          }

          trimmingResult = { success: true };
        }

      } catch (multiJointError) {
        console.error('renderStore: Error in multi-joint rendering system', {
          jointId: joint.id,
          error: multiJointError instanceof Error ? multiJointError.message : String(multiJointError)
        });
        PerformanceMonitor.trackError(joint.id, 'processJoint', 'enhanced_trimming_error');
        return false; // Trigger complete fallback
      }

      // Phase 3: Commit all cached geometry to renderStore
      try {
        wallGeometryCache.commitToRenderStore();
      } catch (commitError) {
        console.error('renderStore: Error committing joint geometry', {
          jointId: joint.id,
          error: commitError instanceof Error ? commitError.message : String(commitError)
        });
        PerformanceMonitor.trackError(joint.id, 'processJoint', 'commit_error');
        // Clear joint cache to prevent stale geometry from being committed later
        wallGeometryCache.clearJoint(joint.id);
        // This is a critical error - return false to trigger fallback
        return false;
      }

      // Phase 4: Clean up
      jointCoordinator.markJointClean(joint.id);
      wallGeometryCache.clearJoint(joint.id);

      const totalTime = performance.now() - startTime;

      // Track performance
      PerformanceMonitor.trackDragEvent(joint.id, 'processJoint_complete', totalTime);

      if (totalTime > 50) {
        this.addPerformanceAlert(`Slow joint processing for ${joint.id}: ${totalTime.toFixed(2)}ms`);
      }

      return true; // Success
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('renderStore: Critical error in processJoint', {
        jointId: joint.id,
        error: err.message,
        operationId,
        duration: (performance.now() - startTime).toFixed(2) + 'ms'
      });
      PerformanceMonitor.trackError(joint.id, 'processJoint', 'critical_error');

      // Clean up cache on error
      try {
        wallGeometryCache.clearJoint(joint.id);
      } catch (cleanupError) {
        console.error('renderStore: Error cleaning up joint cache', { jointId: joint.id });
      }

      return false; // Failure - caller should fall back to normal trimming
    } finally {
      this.jointProcessingDepth = Math.max(0, this.jointProcessingDepth - 1);
    }
  }

  /**
   * Enable or disable progressive rendering mode
   */
  setProgressiveMode = (enabled: boolean) => {
    this.progressiveModeEnabled = enabled;
    const manager = this.getWallUpdateManager();
    if (manager) {
      manager.enableProgressiveMode(enabled);
    }
  }

  clearAlignmentLines = () => {
    this.alignmentLinesMap.clear();
    this.alignmentLinesApproxMap.clear();
  }

  addAlignmentLine = (line: AlignmentLineType, approx: boolean) => {
    if (approx) {
      this.alignmentLinesApproxMap.set(line.id, line);
    } else {
      this.alignmentLinesMap.set(line.id, line);
    }
  }

  setScene(scene: THREE.Scene) {
    this.scene = scene;
  }

  // Helper to clone the scene for off-screen rendering
  // We must handle TroikaText objects specially because standard cloning creates new instances
  // that need async generation, causing them to disappear from immediate synchronous renders.
  // TransformControls must be skipped: Object3D.clone() constructs them without a camera, and
  // their updateMatrixWorld then throws (camera.updateMatrixWorld on undefined) during export.
  cloneScene(originalScene: THREE.Scene) {
    const newScene = new THREE.Scene();
    newScene.copy(originalScene, false);

    const cloneRecursive = (original: THREE.Object3D): THREE.Object3D => {
      let clone: THREE.Object3D;

      // Check for Troika Text object (heuristic: has textRenderInfo)
      // We convert it to a static Mesh to preserve geometry without triggering async text layout
      if (original instanceof THREE.Mesh && (original as any).textRenderInfo) {
        clone = new THREE.Mesh(original.geometry, original.material);
        clone.copy(original, false);
      } else {
        // Shallow clone of the object itself
        clone = original.clone(false);
      }

      // Recursively clone children
      for (const child of original.children) {
        if (!child || shouldSkipObjectForSceneClone(child)) {
          continue;
        }
        clone.add(cloneRecursive(child));
      }

      return clone;
    };

    // copy children
    for (const child of originalScene.children) {
      if (!child || shouldSkipObjectForSceneClone(child)) {
        continue;
      }
      newScene.add(cloneRecursive(child));
    }

    return newScene;
  }

  // Helper to duplicate the camera
  duplicateCamera(originalCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera) {
    const newCamera = originalCamera.clone() as typeof originalCamera;
    newCamera.position.copy(originalCamera.position);
    newCamera.zoom = originalCamera.zoom;
    newCamera.updateProjectionMatrix();
    return newCamera;
  }

  private waitForAnimationFrame = () =>
    new Promise<void>((resolve) => {
      if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        setTimeout(resolve, 16);
        return;
      }

      window.requestAnimationFrame(() => resolve());
    });

  private waitForAnimationFrames = async (frameCount: number) => {
    for (let index = 0; index < frameCount; index += 1) {
      await this.waitForAnimationFrame();
    }
  };

  private waitForSnapshotSceneReady = async () => {
    // Export quality depends on the live scene having fully applied preview-mode
    // changes first. Fixed sleeps were sensitive to slower machines/GPUs and could
    // clone the scene before text/material updates had settled.
    await this.waitForAnimationFrames(1);
    invalidate();
    await this.waitForAnimationFrames(2);
    await new Promise(resolve => setTimeout(resolve, 50));
    invalidate();
    await this.waitForAnimationFrames(1);
  };

  captureSnapshot = async (
    operation = 'thumbnail',
    target = {
      format: 'pdf',
      orientation: 'portrait' as 'portrait' | 'landscape',
      width: 1024,
      height: 768,
    },
    areaToRender?: THREE.Box3,
    camera?: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    annotationOptions?: ExportAnnotationOptions,
  ) => {
    // Note: camera must be passed as parameter to avoid cross-store import
    if (!this.scene || !camera) return;

    const captureId = ++this.captureSnapshotSeq;
    this.captureSnapshotActive++;
    const editorStore = getEditorStore();
    if (this.captureSnapshotActive === 1) {
      editorStore?.setPreviewMode(true, 1.0);
      this.setRendering(true);
    }

    await this.waitForSnapshotSceneReady();

    let offScreenRenderer: THREE.WebGLRenderer | undefined;

    try {
      const outputWidth = target.width || 800;
      const outputHeight = target.height || 600;
      // For exports, preserve the live camera distance to avoid perspective parallax shifts

      // Clone the camera to avoid modifying the original
      const offScreenCamera = this.duplicateCamera(camera) as
        THREE.PerspectiveCamera | THREE.OrthographicCamera;

      // Renderer setup — preview, exports, and upload use 3x supersampling; thumbnails stay 1x.
      const preferredResolutionMultiplier =
        operation === 'downloadPDF' ||
        operation === 'downloadPNG' ||
        operation === 'downloadJPG' ||
        operation === 'upload' ||
        operation === 'preview'
          ? 3
          : 1;
      const { renderWidth, renderHeight, renderScale } = getSafeExportRenderSize(
        outputWidth,
        outputHeight,
        preferredResolutionMultiplier,
      );
      const renderSceneHeight = renderHeight;
      const renderAspect = renderWidth / renderSceneHeight;

      // Clone the scene and filter out the grid and other non-renderable objects
      const offScreenScene = this.cloneScene(this.scene);
      offScreenScene.traverse((object) => {
        if (object instanceof THREE.GridHelper) { // || object.userData.isWallLength
          object.visible = false;
        }

        // Fix LineMaterial resolution and linewidth for high-res rendering
        // This prevents "washed out" lines where the AA edge is scaled up along with the line
        if (object instanceof THREE.Mesh) {
          const material = object.material as any;
          // Check if it's a LineMaterial (has resolution and linewidth properties)
          if (material && material.resolution && material.linewidth !== undefined) {
            // Clone material to avoid affecting live scene
            const newMaterial = material.clone();

            // Update resolution to match the export render target
            // This ensures anti-aliasing is calculated correctly for the high-res buffer
            // preventing "fuzzy" or washed out lines
            newMaterial.resolution.set(renderWidth, renderSceneHeight);

            // Scale linewidth to maintain relative thickness
            // Since we increased resolution, we must strictly increase linewidth
            // to maintain the same physical coverage
            newMaterial.linewidth = material.linewidth * renderScale;

            // For exports, we might want slightly crisper/thicker lines to ensure visibility
            // caused by downsampling. Optional: slight boost
            // newMaterial.linewidth *= 1.0; 

            object.material = newMaterial;
          }
        }
        // Handle standard Lines (LineBasicMaterial/LineDashedMaterial) which SimpleLineSegments uses
        else if (object instanceof THREE.Line) {
          const material = object.material as THREE.LineBasicMaterial;
          if (material && material.linewidth !== undefined) {
            const newMaterial = material.clone();
            // Scale linewidth to maintain relative thickness
            newMaterial.linewidth = material.linewidth * renderScale;
            object.material = newMaterial;
          }
        }
      });



      // Create renderer with high-quality settings for all exports
      // Use antialiasing for all operations to ensure readable text
      offScreenRenderer = new THREE.WebGLRenderer({
        preserveDrawingBuffer: true,
        antialias: true, // Keep antialiasing enabled for readable text
        powerPreference: "high-performance", // Use dedicated GPU if available
        precision: "highp", // Use high precision for better quality
        alpha: false, // Opaque background for exports
        stencil: false, // Not needed for exports
        depth: true, // Enable depth buffer
      });
      offScreenRenderer.setSize(renderWidth, renderSceneHeight);
      offScreenRenderer.setPixelRatio(1); // Use 1 since we're already rendering at higher resolution
      offScreenRenderer.setClearColor(0xffffff, 1);

      // Compute the bounding box of the scene
      if (!areaToRender) {
        areaToRender = this.computeBoundingBoxForScene(offScreenScene);
      }
      const center = areaToRender.getCenter(new THREE.Vector3());
      const size = areaToRender.getSize(new THREE.Vector3());

      if (offScreenCamera instanceof THREE.OrthographicCamera) {
        const halfHeight = Math.max(Math.abs(offScreenCamera.top - offScreenCamera.bottom) / 2, 0.001);
        const halfWidth = halfHeight * renderAspect;
        offScreenCamera.left = -halfWidth;
        offScreenCamera.right = halfWidth;
        offScreenCamera.top = halfHeight;
        offScreenCamera.bottom = -halfHeight;
        offScreenCamera.position.set(center.x, center.y, camera.position.z);
        offScreenCamera.lookAt(center.x, center.y, center.z);
        offScreenCamera.zoom = Math.min(
          (2 * halfWidth) / Math.max(size.x, 1e-6),
          (2 * halfHeight) / Math.max(size.y, 1e-6),
        );
      } else {
        offScreenCamera.aspect = renderAspect;
        // Preserve live camera distance to prevent parallax-induced shifts during export
        const fovY = THREE.MathUtils.degToRad(offScreenCamera.fov);
        const distance = Math.abs(camera.position.z - center.z);
        const tanY = Math.tan(fovY / 2);
        const tanX = tanY * offScreenCamera.aspect;
        offScreenCamera.position.set(center.x, center.y, center.z + distance);
        offScreenCamera.lookAt(center.x, center.y, center.z);
        offScreenCamera.zoom = Math.min(
          (2 * distance * tanX) / size.x,
          (2 * distance * tanY) / size.y,
        );
      }
      offScreenCamera.updateProjectionMatrix();

      // Render the scene
      offScreenRenderer.render(offScreenScene, offScreenCamera);

      // Get the image data at the rendered resolution
      const canvas = offScreenRenderer.domElement;

      // Store both the high-resolution image and the target dimensions
      // The downloadFile method will handle scaling if needed
      const outputCanvas = annotationOptions
        ? composeExportWithTitleBlock(canvas, renderWidth, renderHeight, annotationOptions)
        : canvas;

      if (captureId !== this.captureSnapshotSeq) {
        return;
      }

      this.imageData = outputCanvas.toDataURL();
      // Store resolution info for download scaling
      (this as any).imageDataWidth = outputCanvas.width;
      (this as any).imageDataHeight = outputCanvas.height;
      (this as any).targetWidth = outputWidth;
      (this as any).targetHeight = outputHeight;

      // Handle download or upload
      if (operation === 'downloadPDF' || operation === 'downloadPNG' || operation === 'downloadJPG') {
        this.downloadFile(operation, target);
      } else if (operation === 'upload') {
        // For uploads, use the high-resolution image directly
        // User feedback indicates that downscaling causes thin lines to disappear or look washed out
        // The destination app handles high-res images better than our browser-side downscaling
        const imageWidth = (this as any).imageDataWidth || outputWidth;
        const imageHeight = (this as any).imageDataHeight || outputHeight;

        let uploadImageData = this.imageData;

        // Ensure white background (renderer is mostly opaque but safe to enforce via canvas)
        const canvas = document.createElement('canvas');
        canvas.width = imageWidth;
        canvas.height = imageHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, imageWidth, imageHeight);

          const img = new Image();
          img.src = this.imageData;
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              // Draw at full resolution
              ctx.drawImage(img, 0, 0, imageWidth, imageHeight);
              uploadImageData = canvas.toDataURL('image/png');
              resolve();
            };
            img.onerror = reject;
          });
        }

        const file = await dataUrlToFile(uploadImageData, `floorplan_${Date.now()}.png`);
        const uploadedFloorplanId = getFloorplannerStore().id;
        try {
          const result = await this.client?.mutate<EditFloorplanMutation>({
            variables: { id: uploadedFloorplanId, image: file },
            mutation: EditFloorplanDocument,
          });
          // This image-only write bumps the server's updatedAt. Without feeding
          // the new value back, the next save would be rejected as stale. Skip it
          // if the user switched plans while the upload was in flight.
          if (getFloorplannerStore().id === uploadedFloorplanId) {
            getFloorplannerStore().noteServerUpdatedAt(
              result?.data?.editFloorplan?.updatedAt,
            );
          }
        } catch (error) {
          console.error('Error uploading snapshot: ', error);
        }
      }
    } finally {
      offScreenRenderer?.dispose();
      offScreenRenderer?.forceContextLoss();
      this.captureSnapshotActive--;
      if (this.captureSnapshotActive === 0) {
        editorStore?.setPreviewMode(false);
        this.setRendering(false);
      }
    }
  };

  capture3DSnapshot = async (
    operation = 'thumbnail',
    target = {
      format: 'pdf',
      orientation: 'portrait' as 'portrait' | 'landscape',
      width: 1024,
      height: 768,
    },
    camera?: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    annotationOptions?: ExportAnnotationOptions,
    previewFraming?: ExportPreviewFraming,
  ) => {
    if (!this.scene || !camera) return;

    const captureId = ++this.captureSnapshotSeq;
    this.captureSnapshotActive++;
    if (this.captureSnapshotActive === 1) {
      this.setRendering(true);
    }

    await new Promise(resolve => setTimeout(resolve, 50));
    invalidate();
    await new Promise(resolve => setTimeout(resolve, 100));

    let offScreenRenderer: THREE.WebGLRenderer | undefined;

    try {
      const outputWidth = target.width || 800;
      const outputHeight = target.height || 600;
      // AI render uploads the snapshot to GraphQL; 3x paper PNGs 413 at the
      // proxy, and Recraft rejects sides over 4096px. Capture at 1x, capped.
      const preferredResolutionMultiplier =
        operation === "aiRender"
          ? 1
          : operation === 'downloadPDF' ||
            operation === 'downloadPNG' ||
            operation === 'downloadJPG' ||
            operation === 'upload' ||
            operation === 'preview'
          ? 3
          : 1;
      const { renderWidth, renderHeight } = getSafeExportRenderSize(
        outputWidth,
        outputHeight,
        preferredResolutionMultiplier,
        operation === "aiRender"
          ? {
              maxCanvasDimension: 4096,
              maxCanvasPixels: 4096 * 4096,
            }
          : undefined,
      );

      const offScreenScene = this.cloneScene(this.scene);
      offScreenScene.traverse((object) => {
        if (object instanceof THREE.GridHelper) {
          object.visible = false;
        }
      });

      offScreenRenderer = new THREE.WebGLRenderer({
        preserveDrawingBuffer: true,
        antialias: true,
        powerPreference: "high-performance",
        precision: "highp",
        alpha: false,
        stencil: false,
        depth: true,
      });
      offScreenRenderer.setSize(renderWidth, renderHeight);
      offScreenRenderer.setPixelRatio(1);
      offScreenRenderer.setClearColor(0xffffff, 1);
      offScreenRenderer.shadowMap.enabled = true;
      offScreenRenderer.shadowMap.type = THREE.PCFShadowMap;

      const offScreenCamera = this.duplicateCamera(camera) as THREE.PerspectiveCamera | THREE.OrthographicCamera;
      if (offScreenCamera instanceof THREE.PerspectiveCamera) {
        offScreenCamera.aspect = renderWidth / renderHeight;
      } else {
        const halfHeight = Math.max((offScreenCamera.top - offScreenCamera.bottom) / 2, 0.001);
        const halfWidth = halfHeight * (renderWidth / renderHeight);
        offScreenCamera.left = -halfWidth;
        offScreenCamera.right = halfWidth;
      }
      offScreenCamera.updateProjectionMatrix();
      offScreenRenderer.render(offScreenScene, offScreenCamera);

      const canvas = offScreenRenderer.domElement;
      const outputCanvas = annotationOptions
        ? composeExportWithTitleBlock(canvas, renderWidth, renderHeight, annotationOptions)
        : canvas;

      let imageData = outputCanvas.toDataURL();
      let imageDataWidth = outputCanvas.width;
      let imageDataHeight = outputCanvas.height;

      if (previewFraming && operation !== 'preview') {
        const cropped = await cropExportPreviewImage(imageData, previewFraming);
        imageData = cropped.dataUrl;
        imageDataWidth = cropped.width;
        imageDataHeight = cropped.height;
      }

      if (captureId !== this.captureSnapshotSeq) {
        return;
      }

      this.imageData = imageData;
      (this as any).imageDataWidth = imageDataWidth;
      (this as any).imageDataHeight = imageDataHeight;
      (this as any).targetWidth = outputWidth;
      (this as any).targetHeight = outputHeight;

      if (operation === 'downloadPDF' || operation === 'downloadPNG' || operation === 'downloadJPG') {
        this.downloadFile(operation, target);
      } else if (operation === 'upload') {
        const imageWidth = imageDataWidth || outputWidth;
        const imageHeight = imageDataHeight || outputHeight;
        let uploadImageData = imageData;
        const canvas = document.createElement('canvas');
        canvas.width = imageWidth;
        canvas.height = imageHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, imageWidth, imageHeight);

          const img = new Image();
          img.src = imageData;
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              ctx.drawImage(img, 0, 0, imageWidth, imageHeight);
              uploadImageData = canvas.toDataURL('image/png');
              resolve();
            };
            img.onerror = reject;
          });
        }

        const file = await dataUrlToFile(uploadImageData, `floorplan_3d_${Date.now()}.png`);
        const uploadedFloorplanId = getFloorplannerStore().id;
        try {
          const result = await this.client?.mutate<EditFloorplanMutation>({
            variables: { id: uploadedFloorplanId, image: file },
            mutation: EditFloorplanDocument,
          });
          // This image-only write bumps the server's updatedAt. Without feeding
          // the new value back, the next save would be rejected as stale. Skip it
          // if the user switched plans while the upload was in flight.
          if (getFloorplannerStore().id === uploadedFloorplanId) {
            getFloorplannerStore().noteServerUpdatedAt(
              result?.data?.editFloorplan?.updatedAt,
            );
          }
        } catch (error) {
          console.error('Error uploading 3D snapshot: ', error);
        }
      }
    } finally {
      offScreenRenderer?.dispose();
      offScreenRenderer?.forceContextLoss();
      this.captureSnapshotActive--;
      if (this.captureSnapshotActive === 0) {
        this.setRendering(false);
      }
    }
  };

  computeBoundingBoxForScene(scene: THREE.Scene) {
    let meshCount = 0;
    let validBoxCount = 0;
    let skippedCount = 0;

    function getObjectBox(object: THREE.Object3D) {
      if (!object) return null;
      if ([
        'panning-plane',
        'selection-plane',
        'wall-construction-plane',
        'line-construction-plane',
        'ruler-construction-plane',
        'area-construction-plane'].includes(object.userData.id)) {
        skippedCount++;
        return null;
      }
      const box = new THREE.Box3().setFromObject(object);
      if (!isNaN(box.min.x) && !isNaN(box.min.y) && !isNaN(box.min.z)) {
        return box;
      } else {
        // object does not have a position so calculate the bounding box manually
        const box = new THREE.Box3();
        object.traverse(child => {
          if (child instanceof THREE.Mesh) {
            const geometry = child.geometry;
            geometry.computeBoundingBox();
            const childBox = geometry.boundingBox?.clone();
            if (childBox) {
              childBox.applyMatrix4(child.matrixWorld);
              box.union(childBox);
            }
          }
        });
      }
      if (!isNaN(box.min.x) && !isNaN(box.min.y) && !isNaN(box.min.z)) return box;
      else return null;
    }
    const box = new THREE.Box3();
    scene.traverse(object => {
      if (object instanceof THREE.Mesh) {
        meshCount++;
        const objectBox = getObjectBox(object);
        if (objectBox) {
          validBoxCount++;
          box.union(objectBox);
        }
      } else if (object instanceof THREE.Group) {
        object.children.forEach(child => {
          if (child instanceof THREE.Mesh) {
            meshCount++;
            const objectBox = getObjectBox(child);
            if (objectBox) {
              validBoxCount++;
              box.union(objectBox);
            }
          } else if (child instanceof THREE.Group) {
            child.children.forEach(grandChild => {
              if (grandChild instanceof THREE.Mesh) {
                meshCount++;
                const objectBox = getObjectBox(grandChild);
                if (objectBox) {
                  validBoxCount++;
                  box.union(objectBox);
                }
              }
            });
          }
        });
      }
    });

    // Filter out invalid bounding boxes (zero or near-zero size, or extremely large)
    // Use a very small minimum to allow tiny floorplans (but not zero)
    const MIN_VALID_SIZE = 0.0001; // 0.1mm minimum (very permissive)
    const MAX_VALID_SIZE = 20000; // 20km maximum (allow very large floorplans like 4900m tall)

    const sizeX = box.max.x - box.min.x;
    const sizeY = box.max.y - box.min.y;
    const sizeZ = box.max.z - box.min.z;
    const isValidSize = sizeX >= MIN_VALID_SIZE && sizeY >= MIN_VALID_SIZE &&
      sizeX <= MAX_VALID_SIZE && sizeY <= MAX_VALID_SIZE;

    this.lastBoundingBoxStats = {
      meshCount,
      validBoxCount,
      skippedCount,
      size: { x: sizeX, y: sizeY, z: sizeZ },
      isValidSize,
      isEmpty: box.isEmpty(),
      timestamp: Date.now(),
    };

    if (!isValidSize && !box.isEmpty()) {
      console.warn("[computeBoundingBoxForScene] Invalid bounding box size detected:", {
        sizeX: sizeX.toFixed(6),
        sizeY: sizeY.toFixed(6),
        min: { x: box.min.x.toFixed(2), y: box.min.y.toFixed(2) },
        max: { x: box.max.x.toFixed(2), y: box.max.y.toFixed(2) },
        totalMeshes: meshCount,
        validBoxes: validBoxCount,
        reason: sizeX < MIN_VALID_SIZE ? "too small X" :
          sizeY < MIN_VALID_SIZE ? "too small Y" :
            sizeX > MAX_VALID_SIZE ? "too large X" :
              sizeY > MAX_VALID_SIZE ? "too large Y" : "unknown"
      });
      // Return empty box if size is invalid - this will trigger fallback behavior
      return new THREE.Box3();
    }

    if (this.debugLogging || isFloorplannerDebugLoggingEnabled()) {
      console.log("[computeBoundingBoxForScene] Statistics:", {
        totalMeshes: meshCount,
        validBoxes: validBoxCount,
        skippedObjects: skippedCount,
        finalBox: box.isEmpty() ? "empty" : {
          min: { x: box.min.x.toFixed(2), y: box.min.y.toFixed(2), z: box.min.z.toFixed(2) },
          max: { x: box.max.x.toFixed(2), y: box.max.y.toFixed(2), z: box.max.z.toFixed(2) },
          size: {
            x: sizeX.toFixed(2),
            y: sizeY.toFixed(2),
            z: sizeZ.toFixed(2)
          },
          center: box.isEmpty() ? "N/A" : {
            x: ((box.min.x + box.max.x) / 2).toFixed(2),
            y: ((box.min.y + box.max.y) / 2).toFixed(2),
            z: ((box.min.z + box.max.z) / 2).toFixed(2)
          },
          isValidSize
        }
      });
    }

    return box;
  }

  /**
   * Sanitizes a user-provided project name to a safe file name.
   * - Keeps letters, numbers, space, underscores and dashes
   * - Trims and collapses whitespace into single underscores
   * - Falls back to "floorplan" if the result is empty
   */
  private sanitizeFileName(name: string): string {
    const cleaned = (name || '')
      .replace(/[^a-z0-9_\- ]+/gi, '')
      .trim()
      .replace(/\s+/g, '_');
    return cleaned.length > 0 ? cleaned : 'floorplan';
  }

  downloadFile = async (
    operation: string,
    resolution: TargetFormatType,
  ) => {
    if (this.imageData !== undefined) {
      // Determine the file name from the current BONI project/floorplan name
      // We sanitize it to avoid invalid path characters across platforms
      const fileName = this.sanitizeFileName(getFloorplannerStore().name);
      const targetWidth = resolution.width;
      const targetHeight = resolution.height;

      // Get the actual rendered image dimensions (may be higher resolution)
      const imageWidth = (this as any).imageDataWidth || targetWidth;
      const imageHeight = (this as any).imageDataHeight || targetHeight;
      const isHighRes = imageWidth > targetWidth || imageHeight > targetHeight;

      const pdf = new jsPDF(
        resolution.orientation,
        'px',
        resolution.format
      );

      if (operation === 'downloadPDF') {
        // For PDF, convert to JPG format for smaller file size (same quality, no transparency needed)
        const imageWidth = (this as any).imageDataWidth || targetWidth;
        const imageHeight = (this as any).imageDataHeight || targetHeight;
        const isHighRes = imageWidth > targetWidth || imageHeight > targetHeight;

        // Convert image to JPG format with maximum quality
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (ctx) {
          if (isHighRes) {
            // Use high-resolution image directly, let jsPDF handle scaling for better quality
            canvas.width = imageWidth;
            canvas.height = imageHeight;
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, imageWidth, imageHeight);

            const img = new Image();
            img.src = this.imageData;
            await new Promise<void>((resolve, reject) => {
              img.onload = () => {
                ctx.drawImage(img, 0, 0, imageWidth, imageHeight);
                // Convert to JPG with maximum quality (1.0) for PDF - jsPDF will scale it down
                const jpgDataUrl = canvas.toDataURL('image/jpeg', 1.0);
                const page = pdf.addPage([targetWidth, targetHeight], resolution.orientation);
                // Let jsPDF scale the high-res image down - this provides better quality
                page.addImage(jpgDataUrl, 'JPEG', 0, 0, targetWidth, targetHeight, undefined, 'FAST');
                // deletes the first page of the pdf document jsPDF adds an empty first page :O
                pdf.deletePage(1);
                pdf.save(fileName + '.pdf');
                resolve();
              };
              img.onerror = reject;
            });
          } else {
            // Fallback for non-export operations
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, targetWidth, targetHeight);

            const img = new Image();
            img.src = this.imageData;
            await new Promise<void>((resolve, reject) => {
              img.onload = () => {
                ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
                // Convert to JPG with maximum quality (1.0) for PDF
                const jpgDataUrl = canvas.toDataURL('image/jpeg', 1.0);
                const page = pdf.addPage([targetWidth, targetHeight], resolution.orientation);
                page.addImage(jpgDataUrl, 'JPEG', 0, 0, targetWidth, targetHeight, undefined, 'FAST');
                // deletes the first page of the pdf document jsPDF adds an empty first page :O
                pdf.deletePage(1);
                pdf.save(fileName + '.pdf');
                resolve();
              };
              img.onerror = reject;
            });
          }
        }
      } else if (operation === 'downloadPNG') {
        const link = document.createElement('a')
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (ctx) {
          if (isHighRes) {
            // Use high-resolution image directly for better quality
            canvas.width = imageWidth
            canvas.height = imageHeight
            ctx.fillStyle = 'white'
            ctx.fillRect(0, 0, imageWidth, imageHeight)
            const img = new Image()
            img.src = this.imageData
            img.onload = () => {
              ctx.drawImage(img, 0, 0, imageWidth, imageHeight)
              link.href = canvas.toDataURL('image/png')
              link.download = fileName + '.png'
              link.click()
            }
          } else {
            // Fallback for non-export operations
            canvas.width = targetWidth
            canvas.height = targetHeight
            ctx.fillStyle = 'white'
            ctx.fillRect(0, 0, targetWidth, targetHeight)
            const img = new Image()
            img.src = this.imageData
            img.onload = () => {
              ctx.drawImage(img, 0, 0, targetWidth, targetHeight)
              link.href = canvas.toDataURL('image/png')
              link.download = fileName + '.png'
              link.click()
            }
          }
        }
      } else if (operation === 'downloadJPG') {
        const link = document.createElement('a')
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (ctx) {
          if (isHighRes) {
            // Use high-resolution image directly for better quality
            canvas.width = imageWidth
            canvas.height = imageHeight
            ctx.fillStyle = 'white'
            ctx.fillRect(0, 0, imageWidth, imageHeight)
            const img = new Image()
            img.src = this.imageData
            img.onload = () => {
              ctx.drawImage(img, 0, 0, imageWidth, imageHeight)
              // Use high quality (0.95) to match PNG quality while reducing file size
              link.href = canvas.toDataURL('image/jpeg', 0.95)
              link.download = fileName + '.jpg'
              link.click()
            }
          } else {
            // Fallback for non-export operations
            canvas.width = targetWidth
            canvas.height = targetHeight
            ctx.fillStyle = 'white'
            ctx.fillRect(0, 0, targetWidth, targetHeight)
            const img = new Image()
            img.src = this.imageData
            img.onload = () => {
              ctx.drawImage(img, 0, 0, targetWidth, targetHeight)
              // Use high quality (0.95) to match PNG quality while reducing file size
              link.href = canvas.toDataURL('image/jpeg', 0.95)
              link.download = fileName + '.jpg'
              link.click()
            }
          }
        }
      }
    }
  }

  setRendering(rendering: boolean) {
    this.rendering = rendering;
  }

  setIsDragging(dragging: boolean) {
    this.isDragging = dragging;
  }

  /**
   * Begin group translate mode for optimized group drag.
   * During translate mode, objects are visually offset without MobX updates.
   * This avoids expensive geometry recalculations during drag.
   */
  beginGroupTranslate(objectIds: string[], wallIds: string[]) {
    this.isGroupTranslating = true;
    this.groupTranslateOffset = { x: 0, y: 0 };
    this.translatingObjectIds = new Set(objectIds);
    this.translatingWallIds = new Set(wallIds);
    // Block dirty and enable dragging state
    this.setIsDragging(true);
    this.setBlockDirty(true);
  }

  /**
   * Update the group translate offset.
   * This triggers a render but not geometry recalculation.
   */
  updateGroupTranslateOffset(deltaX: number, deltaY: number) {
    this.groupTranslateOffset = { x: deltaX, y: deltaY };
    // Request render without geometry recalc
    invalidate();
  }

  /**
   * End group translate mode.
   * After this, final positions should be committed to MobX.
   */
  endGroupTranslate() {
    this.isGroupTranslating = false;
    this.groupTranslateOffset = { x: 0, y: 0 };
    this.translatingObjectIds.clear();
    this.translatingWallIds.clear();
    this.setIsDragging(false);
    this.setBlockDirty(false);
  }

  /**
   * Check if an object is currently being translated.
   */
  isObjectTranslating(id: string): boolean {
    return this.isGroupTranslating && this.translatingObjectIds.has(id);
  }

  /**
   * Check if a wall is currently being translated.
   */
  isWallTranslating(wallId: string): boolean {
    return this.isGroupTranslating && this.translatingWallIds.has(wallId);
  }

  /**
   * Begin group rotate mode for optimized group rotation.
   * During rotate mode, objects are visually rotated without MobX updates.
   * This avoids expensive geometry recalculations during drag.
   */
  beginGroupRotate(objectIds: string[], wallIds: string[], center: { x: number; y: number }) {
    this.isGroupRotating = true;
    this.groupRotationAngle = 0;
    this.groupRotationCenter = { x: center.x, y: center.y };
    this.rotatingObjectIds = new Set(objectIds);
    this.rotatingWallIds = new Set(wallIds);
    // Block dirty and enable dragging state
    this.setIsDragging(true);
    this.setBlockDirty(true);
  }

  /**
   * Update the group rotation angle.
   * This triggers a render but not geometry recalculation.
   */
  updateGroupRotateAngle(angle: number) {
    this.groupRotationAngle = angle;
    // Request render without geometry recalc
    invalidate();
  }

  /**
   * End group rotate mode.
   * After this, final positions should be committed to MobX.
   */
  endGroupRotate() {
    this.isGroupRotating = false;
    this.groupRotationAngle = 0;
    this.groupRotationCenter = { x: 0, y: 0 };
    this.rotatingObjectIds.clear();
    this.rotatingWallIds.clear();
    this.setIsDragging(false);
    this.setBlockDirty(false);
  }

  /**
   * Check if an object is currently being rotated.
   */
  isObjectRotating(id: string): boolean {
    return this.isGroupRotating && this.rotatingObjectIds.has(id);
  }

  /**
   * Check if a wall is currently being rotated.
   */
  isWallRotating(wallId: string): boolean {
    return this.isGroupRotating && this.rotatingWallIds.has(wallId);
  }

  /**
   * Get the rotated position of a point around the rotation center.
   * Returns original position if not in rotation mode.
   */
  getRotatedPosition(x: number, y: number): { x: number; y: number } {
    if (!this.isGroupRotating) return { x, y };
    const dx = x - this.groupRotationCenter.x;
    const dy = y - this.groupRotationCenter.y;
    const cos = Math.cos(this.groupRotationAngle);
    const sin = Math.sin(this.groupRotationAngle);
    return {
      x: this.groupRotationCenter.x + dx * cos - dy * sin,
      y: this.groupRotationCenter.y + dx * sin + dy * cos
    };
  }

  setDirty() {
    this.isDirty = true;
  }

  setBlockDirty(block: boolean) {
    this.blockDirty = block;
  }

  setIsReconstructingShapes(reconstructing: boolean) {
    this.isReconstructingShapes = reconstructing;
  }

  setSafeToRedraw(safe: boolean, wallId?: string) {
    // Allow selective redraw for specific walls during deferred mode
    if (safe || (wallId && this.allowVisualUpdatesForWalls.has(wallId))) {
      this.safeToRedraw = true;
    } else if (!safe) {
      this.safeToRedraw = false;
    }
  }

  /**
   * Begin multi-joint rendering batch - inhibits intermediate renders
   */
  beginMultiJointRendering() {
    this.multiJointRenderingDepth++;
    if (this.multiJointRenderingDepth === 1) {
      // Inhibit rendering during multi-joint calculations
      this.setSafeToRedraw(false);
      this.setBlockDirty(true);
    }
  }

  /**
   * End multi-joint rendering batch - allows rendering again
   */
  endMultiJointRendering() {
    if (this.multiJointRenderingDepth <= 0) return;

    this.multiJointRenderingDepth--;
    if (this.multiJointRenderingDepth === 0) {
      // Re-enable rendering after all multi-joint operations complete
      this.setSafeToRedraw(true);
      this.setBlockDirty(false);

      // Flush any deferred invalidations first
      this.flushDeferredInvalidations();
    }
  }

  /**
   * Check if currently in multi-joint rendering mode
   */
  isMultiJointRenderingActive(): boolean {
    return this.multiJointRenderingDepth > 0;
  }

  /**
   * Request invalidation for a wall, respecting multi-joint rendering state
   * This method coordinates with React components to prevent intermediate renders
   */
  requestInvalidation(wallId?: string): void {
    // If multi-joint rendering is active, defer the invalidation
    if (this.multiJointRenderingDepth > 0) {
      if (wallId) {
        this.pendingInvalidations.add(wallId);
      }

      // Clear existing timeout and set a new one for batched invalidation
      if (this.deferredInvalidationTimeout !== null) {
        clearTimeout(this.deferredInvalidationTimeout);
      }

      this.deferredInvalidationTimeout = window.setTimeout(() => {
        this.flushDeferredInvalidations();
      }, 16); // One frame delay for React batch completion
    } else {
      // Immediate invalidation when not in multi-joint mode
      invalidate();
    }
  }

  /**
   * Flush all deferred invalidations
   */
  private flushDeferredInvalidations(): void {
    if (this.deferredInvalidationTimeout !== null) {
      clearTimeout(this.deferredInvalidationTimeout);
      this.deferredInvalidationTimeout = null;
    }

    const hadInvalidations = this.pendingInvalidations.size > 0;
    this.pendingInvalidations.clear();

    if (hadInvalidations) {
      invalidate();
    }
  }

  /**
   * Check if a wall should defer its invalidation
   */
  shouldDeferInvalidation(wallId?: string): boolean {
    return this.multiJointRenderingDepth > 0;
  }

  beginDeferredBatch() {
    this.deferredUpdateDepth++;
    if (this.deferredUpdateDepth === 1) {
      this.setSafeToRedraw(false);
      this.deferredDirtyWalls.clear();
      this.deferredSnappingResults.clear();
    }
  }

  endDeferredBatch() {
    if (this.deferredUpdateDepth <= 0) return;
    this.deferredUpdateDepth--;
    if (this.deferredUpdateDepth === 0) {
      this.setSafeToRedraw(true);
      // Apply any deferred snapping results before final redraw
      this.deferredSnappingResults.forEach((snappingData, wallId) => {
        try {
          const wall = getFloorplannerStore().wallsMap.get(wallId);
          if (!wall || !snappingData) {
            return;
          }

          const hasEdgeConnection = (position: "start" | "end"): boolean => {
            return wall.edgeConnections?.some(connection => connection.sourceEndpoint === position) ?? false;
          };

          const startHasEdge = hasEdgeConnection("start");
          const endHasEdge = hasEdgeConnection("end");

          if (startHasEdge || endHasEdge) {
            return;
          }

          if (snappingData.startSnapped && snappingData.newStart) {
            const endToUse = snappingData.endSnapped && snappingData.newEnd ? snappingData.newEnd : wall.end.clone();
            getFloorplannerStore().updateObjectPosition(wallId, snappingData.newStart, endToUse);
          } else if (snappingData.endSnapped && snappingData.newEnd) {
            const startToUse = snappingData.startSnapped && snappingData.newStart ? snappingData.newStart : wall.start.clone();
            getFloorplannerStore().updateObjectPosition(wallId, startToUse, snappingData.newEnd);
          }
        } catch (error) {
          console.error('Error applying deferred snapping results:', error);
        }
      });

      // Only invalidate if there were dirty walls during the batch
      const dirtyWallCount = this.deferredDirtyWalls.size;
      if (dirtyWallCount > 0) {
        invalidate();
      }
      this.deferredDirtyWalls.clear();
      this.allowVisualUpdatesForWalls.clear();
      this.deferredSnappingResults.clear();
      // Trigger garbage collection hint
      if (typeof gc !== 'undefined') gc();
      return dirtyWallCount;
    }
  }

  markWallDirty(wallId: string) {
    if (this.deferredUpdateDepth > 0) {
      this.deferredDirtyWalls.add(wallId);
    }
  }

  /**
   * Allow visual updates for a specific wall during deferred mode
   * This enables dragged walls to show visual feedback while expensive operations are deferred
   */
  allowVisualUpdatesForWall(wallId: string) {
    this.allowVisualUpdatesForWalls.add(wallId);
    // Trigger immediate redraw
    invalidate();
  }

  /**
   * Check if visual updates are allowed for a specific wall
   */
  isVisualUpdateAllowed(wallId: string): boolean {
    return this.safeToRedraw || this.allowVisualUpdatesForWalls.has(wallId);
  }

  /**
   * Disable visual updates for a specific wall during deferred mode
   */
  disallowVisualUpdatesForWall(wallId: string) {
    this.allowVisualUpdatesForWalls.delete(wallId);
    this.deferredSnappingResults.delete(wallId);
  }

  /**
   * Check if any walls are currently in progressive mode
   *
   * This utility method can be used throughout the render store to make progressive mode decisions
   * and optimize operations when drag operations are active.
   */
  isAnyWallInProgressiveMode(): boolean {
    // Also check isDragging as a fallback - the watchdog timer may have cleared
    // progressive mode even though drag is still active (e.g., after OS notification
    // interrupts and the 2-second watchdog fires, but user resumes dragging)
    if (this.isDragging) return true;
    const manager = this.getWallUpdateManager();
    return manager ? manager.isProgressiveModeActive() : false;
  }

  /**
   * Handle geometry errors with specific error categorization and recovery
   */
  handleGeometryError(operation: string, wallId: string, error: Error): void {
    const errorKey = `${operation}_${wallId}`;
    const now = Date.now();

    if (!this.geometryErrors.has(errorKey)) {
      this.geometryErrors.set(errorKey, { count: 0, lastError: '', timestamp: now });
    }

    const errorData = this.geometryErrors.get(errorKey)!;
    errorData.count++;
    errorData.lastError = error.message;
    errorData.timestamp = now;

    // Track error in performance monitor
    PerformanceMonitor.trackError(wallId, operation, 'geometry_error');

    // Log error with context
    console.error('renderStore: Geometry error', {
      operation,
      wallId,
      error: error.message,
      errorCount: errorData.count,
      stack: error.stack
    });
  }

  /**
   * Attempt error recovery for geometry operations
   */
  attemptErrorRecovery(wallId: string, operation: string): boolean {
    const recoveryKey = `${operation}_${wallId}`;
    const maxRecoveryAttempts = 3;

    if (!this.errorRecoveryAttempts.has(recoveryKey)) {
      this.errorRecoveryAttempts.set(recoveryKey, 0);
    }

    const attempts = this.errorRecoveryAttempts.get(recoveryKey)!;
    if (attempts >= maxRecoveryAttempts) {
      console.warn('renderStore: Maximum recovery attempts reached', {
        wallId,
        operation,
        attempts
      });
      return false;
    }

    this.errorRecoveryAttempts.set(recoveryKey, attempts + 1);

    try {
      switch (operation) {
        case 'composition':
          return this.recoverFromCompositionError(wallId);
        case 'trimming':
          return this.recoverFromTrimmingError(wallId);
        default:
          return this.recoverFromGenericError(wallId, operation);
      }
    } catch (recoveryError) {
      const err = recoveryError instanceof Error ? recoveryError : new Error(String(recoveryError));
      console.error('renderStore: Error recovery failed', {
        wallId,
        operation,
        attempts: attempts + 1,
        error: err.message
      });
      return false;
    }
  }

  /**
   * Recover from composition errors by using cached shapes or simplified geometry
   */
  private recoverFromCompositionError(wallId: string): boolean {


    // Try to use cached shapes
    const cachedInnerShapes = this.innerWallShapesMap.get(wallId);
    if (cachedInnerShapes && cachedInnerShapes.length > 0) {

      return true;
    }

    // Try simplified composition
    try {
      const wall = getFloorplannerStore().wallsMap.get(wallId);
      if (wall) {
        // Create basic rectangular shapes as fallback
        const basicShapes: WallShapeType[] = [{
          lineSide: 'inner',
          wall: wall,
          paths: [
            wall.start,
            wall.end,
            wall.end,
            wall.start
          ]
        }];
        basicShapes.forEach(shape => populateDefaultCorners(shape));

        this.innerWallShapesMap.set(wallId, basicShapes);
        return true;
      }
    } catch (simplificationError) {
      const err = simplificationError instanceof Error ? simplificationError : new Error(String(simplificationError));
      console.error('renderStore: Simplified composition recovery failed', {
        wallId,
        error: err.message
      });
    }

    return false;
  }

  /**
   * Recover from trimming errors by using untrimmed shapes
   */
  private recoverFromTrimmingError(wallId: string): boolean {


    // Keep existing shapes without trimming
    const existingShapes = this.innerWallShapesMap.get(wallId);
    if (existingShapes) {

      return true;
    }

    return false;
  }

  /**
   * Generic error recovery for other operations
   */
  private recoverFromGenericError(wallId: string, operation: string): boolean {


    // Clear error-prone cached data
    this.clearWallShapes(wallId);

    // Reset error recovery attempts for this wall
    this.errorRecoveryAttempts.delete(`${operation}_${wallId}`);

    return true;
  }

  /**
   * Add performance alert
   */
  addPerformanceAlert(alert: string): void {
    this.performanceAlerts.add(alert);

    // Keep only last 50 alerts
    if (this.performanceAlerts.size > 50) {
      const alertsArray = Array.from(this.performanceAlerts);
      this.performanceAlerts.clear();
      alertsArray.slice(-25).forEach(a => this.performanceAlerts.add(a));
    }
  }

  /**
   * Get comprehensive error report
   */
  getErrorReport(): object {
    const now = Date.now();

    return {
      timestamp: now,

      // Error summary
      geometryErrors: Object.fromEntries(this.geometryErrors),
      totalErrors: Array.from(this.geometryErrors.values()).reduce((sum, err) => sum + err.count, 0),
      recentErrors: Array.from(this.geometryErrors.entries())
        .filter(([_, data]) => now - data.timestamp < 300000) // Last 5 minutes
        .map(([key, data]) => ({ key, ...data })),

      // Performance alerts
      activePerformanceAlerts: Array.from(this.performanceAlerts),

      // Operation metrics
      operationMetrics: { ...this.operationMetrics },

      // Recovery attempts
      errorRecoveryAttempts: Object.fromEntries(this.errorRecoveryAttempts),
      totalRecoveryAttempts: Array.from(this.errorRecoveryAttempts.values()).reduce((sum, count) => sum + count, 0),

      // Current state
      currentState: {
        isReconstructingShapes: this.isReconstructingShapes,
        safeToRedraw: this.safeToRedraw,
        isDragging: this.isDragging,
        deferredUpdateDepth: this.deferredUpdateDepth,
        deferredDirtyWalls: this.deferredDirtyWalls.size,
        progressiveModeEnabled: this.progressiveModeEnabled
      }
    };
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): object {
    const totalCacheRequests = this.jointPolygonCacheHits + this.jointPolygonCacheMisses;
    const cacheHitRate = totalCacheRequests > 0
      ? (this.jointPolygonCacheHits / totalCacheRequests * 100).toFixed(1) + '%'
      : '0%';

    return {
      operationMetrics: { ...this.operationMetrics },
      averageOperationTime: this.operationMetrics.shapeCompositions + this.operationMetrics.trimOperations > 0 ?
        this.operationMetrics.totalTime / (this.operationMetrics.shapeCompositions + this.operationMetrics.trimOperations) : 0,
      errorRate: this.operationMetrics.shapeCompositions + this.operationMetrics.trimOperations > 0 ?
        (this.operationMetrics.errors / (this.operationMetrics.shapeCompositions + this.operationMetrics.trimOperations) * 100).toFixed(2) + '%' : '0%',

      activeAlerts: this.performanceAlerts.size,

      wallShapeCounts: {
        innerShapes: this.innerWallShapesMap.size,
        outline1Shapes: this.outline1WallShapesMap.size,
        outline2Shapes: this.outline2WallShapesMap.size,
        endCapsShapes: this.endCapsWallShapesMap.size,
        wallClippings: this.wallClippingsMap.size
      },

      jointPolygonCache: {
        hits: this.jointPolygonCacheHits,
        misses: this.jointPolygonCacheMisses,
        hitRate: cacheHitRate,
        totalRequests: totalCacheRequests
      }
    };
  }

  /**
   * Detect performance issues and memory leaks
   */
  detectPerformanceIssues(): string[] {
    const issues: string[] = [];

    // Check error rates
    const totalOperations = this.operationMetrics.shapeCompositions + this.operationMetrics.trimOperations;
    const errorRate = totalOperations > 0 ? (this.operationMetrics.errors / totalOperations) * 100 : 0;

    if (errorRate > 5) {
      issues.push(`High error rate: ${errorRate.toFixed(1)}% (${this.operationMetrics.errors}/${totalOperations})`);
    }

    // Check for memory leaks (excessive shape maps)
    if (this.innerWallShapesMap.size > 1000) {
      issues.push(`Large innerWallShapesMap: ${this.innerWallShapesMap.size} entries`);
    }

    if (this.wallClippingsMap.size > 500) {
      issues.push(`Large wallClippingsMap: ${this.wallClippingsMap.size} entries`);
    }

    // Check for stuck deferred batches
    if (this.deferredUpdateDepth > 0) {
      issues.push(`Stuck deferred batch: depth ${this.deferredUpdateDepth}`);
    }

    // Check for excessive performance alerts
    if (this.performanceAlerts.size > 20) {
      issues.push(`Many performance alerts: ${this.performanceAlerts.size} active`);
    }

    // Check for excessive error recovery attempts
    const totalRecoveryAttempts = Array.from(this.errorRecoveryAttempts.values()).reduce((sum, count) => sum + count, 0);
    if (totalRecoveryAttempts > 50) {
      issues.push(`High error recovery attempts: ${totalRecoveryAttempts} total`);
    }

    return issues;
  }

  /**
   * Store snapping results for visual feedback during drag operations
   * These results are used to provide visual feedback without creating permanent connections
   */
  storeSnappingResult(wallId: string, snappingData: any) {
    this.deferredSnappingResults.set(wallId, snappingData);
  }

  /**
   * Get stored snapping results for a wall
   */
  getSnappingResult(wallId: string): any | undefined {
    return this.deferredSnappingResults.get(wallId);
  }

  /**
   * Clear all error history and reset performance metrics
   */
  clearErrorHistory(): void {
    this.geometryErrors.clear();
    this.performanceAlerts.clear();
    this.errorRecoveryAttempts.clear();
    this.operationMetrics = { trimOperations: 0, shapeCompositions: 0, errors: 0, totalTime: 0 };

    // Clear joint data
    this.clearAllJoints();


  }

  /**
   * Clear geometry cache to free up memory
   */
  clearGeometryCache(): void {
    // Clear all geometry-related maps
    this.wallClippingsMap.clear();
    this.innerWallShapesMap.clear();
    this.outline1WallShapesMap.clear();
    this.outline2WallShapesMap.clear();
    this.endCapsWallShapesMap.clear();
    this.alignmentLinesMap.clear();
    this.alignmentLinesApproxMap.clear();

    // Clear deferred results
    this.deferredSnappingResults.clear();


  }

  /**
   * Mark a wall for fallback update when progressive mode fails
   */
  markWallForFallbackUpdate(wallId: string): void {
    // Add to a set of walls that need fallback updates
    if (!this.wallsNeedingFallback) {
      this.wallsNeedingFallback = new Set<string>();
    }
    this.wallsNeedingFallback.add(wallId);
  }

}

export const renderStore = new RenderStore();
export type RenderStoreType = RenderStore;
export const renderStoreContext = React.createContext<RenderStore>(renderStore);
export const useRenderStore = () => useContext(renderStoreContext);

declare global {
  var __boniRenderStore: RenderStore | undefined;
}

/**
 * Cache the renderStore singleton on the global object so modules that need a
 * deferred reference (to avoid circular dependencies) can safely retrieve it.
 */
if (typeof globalThis !== "undefined") {
  (globalThis as typeof globalThis & { __boniRenderStore?: RenderStore }).__boniRenderStore = renderStore;
}
